import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import i18next from 'i18next'
import { cloneDeep, isEqual, orderBy, padStart } from 'lodash'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { preparePurchaseApi } from 'src/redux/api/graphql/preparePurchaseApi'
import { purchaseApi } from 'src/redux/api/graphql/purchaseApi'
import { fetchSuppliers } from 'src/redux/slices/suppliers'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	ReduxCompany,
	ReduxExportInfo,
	ReduxPreparePurchase,
	ReduxProjects,
	ReduxPurchase,
	ReduxQuote,
	ReduxRecapInfo,
	ReduxS3Object,
	ReduxSuppliers,
} from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	fetchAllPrepareInvoicesByTenantIdUtil,
	fetchAllQuotesByTenantIdUtilForSubTeamAdmin,
	fetchAllQuotesByTenantIdUtilForTeamAdmin,
	fetchChildFreelancerProjectUtil,
	fetchChildFreelancerQuotes,
	fetchProjectsByTenantIdUtil,
	fetchProjectsByTenantIdUtilForSubTeamAdmin,
	fetchProjectsByTenantIdUtilForTeamAdmin,
	fetchPurchasesByTenantIdUtil,
	fetchPurchasesByTenantIdUtilFreelancer,
	fetchQuotesByTenantIdUtil,
	fetchReviewAndExportPrepareInvoicesByTenantIdUtil,
	fetchSuppliersByTenantIdUtil,
	fetchUnpaidExportPurchasesByTenantIdUtil,
	getFetchFilter,
} from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { updateCompanyInfoWithDeltaSyncSuppress } from 'src/utils/miscellaneous'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import {
	aicpSubCategories,
	categories,
	cnc5chSubCategories,
	photoGridSubCategories,
	postProductionCategories,
	quoteAnimationAndLiveActionSubCategories,
	quoteAnimationOnlySubCategories,
	quoteDigitalSubCategories,
	soundProdSubCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import { getSupplierUuids } from 'src/utils/supplierUtils/getSupplierUuids'
import {
	getProperUpdateValues,
	hasBusinessChanges,
} from 'src/utils/updateApiUtil'
import { companyApi } from '@/redux/api/graphql/companyApi'

dayjs.extend(customParseFormat)

type PurchaseState = {
	purchasesData: ReduxPurchase[]
	allPreparePurchasesData: ReduxPreparePurchase[]
	preparePurchasesInExportAndReview: ReduxPreparePurchase[]
	unpaidPurchasesInExport: ReduxPreparePurchase[]
	quoteData: ReduxQuote[]
	projectData: ReduxProjects[]
	importSuppliersInExportInvoicesData: any
	importExportInvoicesResultsData: any
	importExpenseReportResultsData: any
	importErrorData: any
	importSuccessData: any
	importExpenseReportErrorData: any
	importExpenseReportSuccessData: any
	formData: any
	currentPurchaseInfo: ReduxPurchase | null
	currentPreparePurchase: ReduxPreparePurchase | null
	fetchPurchasesApiStatus: ApiStatus
	fetchCurrentPurchaseApiStatus: ApiStatus
	fetchCurrentPreparePurchaseApiStatus: ApiStatus
	fetchAllPreparePurchasesApiStatus: ApiStatus
	fetchReviewAndExportPreparePurchasesApiStatus: ApiStatus
	fetchUnpaidExportPurchasesApiStatus: ApiStatus
	createPurchaseApiStatus: ApiStatus
	createPreparePurchaseApiStatus: ApiStatus
	createOverheadApiStatus: ApiStatus
	updatePurchaseApiStatus: ApiStatus
	updatePaymentApiStatus: ApiStatus
	dailyUpdatePaymentApiStatus: ApiStatus
	updateArchiveApiStatus: ApiStatus
	uploadInvoiceApiStatus: ApiStatus
	updateToggleArchiveApiStatus: ApiStatus
	updateMoveToNextApiStatus: ApiStatus
	updateLastExportedDateApiStatus: ApiStatus
	updateAssignApiStatus: ApiStatus
	updateAssignDirectlyToExportsApiStatus: ApiStatus
	updateMoveToPrevApiStatus: ApiStatus
	updateMoveBackToReviewApiStatus: ApiStatus
	editInvoiceApiStatus: ApiStatus
	editPrepareInvoiceApiStatus: ApiStatus
	deletePurchaseInvoiceApiStatus: ApiStatus
	moveToPrepareFromExportApiStatus: ApiStatus
	markAsToPayApiStatus: ApiStatus
	bulkUndoToPayApiStatus: ApiStatus
	bulkUndoPendingReconApiStatus: ApiStatus
	undoToPayApiStatus: ApiStatus
	bulkMarkAsPaidApiStatus: ApiStatus
	bulkUnmarkAsPaidApiStatus: ApiStatus
	importExportInvoicesInvoiceApiStatus: ApiStatus
	importExpenseReportsInvoiceApiStatus: ApiStatus
	generateBridgePaymentLinkApiStatus: ApiStatus
	updatePaymentStatusAfterBridgePaymentLinkApiStatus: ApiStatus
	changeCurrentPreparePurchaseInvoiceApiStatus: ApiStatus
	toggleSupplierInvoicesFlagInvoiceApiStatus: ApiStatus
	purchaseGridLoading: boolean
	addInvoiceModalOpenStatus: boolean
	newInvoiceModalOpenStatus: boolean
	assignInvoiceModalOpenStatus: boolean
	assignDirectlyToExportsInvoiceModalOpenStatus: boolean
	confirmInvoiceModalOpenStatus: boolean
	moveToPrepareModalOpenStatus: boolean
	moveToReviewOpenStatus: boolean
	addTagsModalOpenStatus: boolean
	addEmailInvoiceModalOpenStatus: boolean
	editInvoiceModalOpenStatus: boolean
	editPrepareInvoiceModalOpenStatus: boolean
	deleteInvoiceDialogOpenStatus: boolean
	purchasePaidConfirmationDialogOpenStatus: boolean
	purchaseUnpaidConfirmationDialogOpenStatus: boolean
	purchaseArchivedConfirmationDialogOpenStatus: boolean
	purchaseDownloadConfirmationDialogOpenStatus: boolean
	prepareInvoiceArchiveConfirmationDialogOpenStatus: boolean
	reviewInvoiceNoCostlineConfirmationDialogOpenStatus: boolean
	supplierRedirectConfirmationDialogOpenStatus: boolean
	removeEmailInvoiceDialogStatus: boolean
	confirmSelectAllInExportDialogOpenStatus: boolean
	confirmExportToPayPreparePurchasesDialogOpenStatus: boolean
	confirmExportPreparePurchaseXMLDialogOpenStatus: boolean
	confirmUnmarkToPayDialogOpenStatus: boolean
	confirmExportDialogOpenStatus: boolean
	importExportInvoicesAddSuppliersModalOpen: boolean
	importExportInvoicesMessageDialogOpen: boolean
	importExpenseReportsMessageDialogOpen: boolean
	importExportInvoicesModalOpen: boolean
	isBulkAction: boolean
	importExpenseReportsModalOpen: boolean
	bridgePaymentLinkDialogOpenStatus: boolean

	error?: string | null
}

const initialState: PurchaseState = {
	purchasesData: [],
	allPreparePurchasesData: [],
	preparePurchasesInExportAndReview: [],
	unpaidPurchasesInExport: [],
	quoteData: [],
	projectData: [],
	importSuppliersInExportInvoicesData: [],
	importExportInvoicesResultsData: [],
	importExpenseReportResultsData: [],
	importErrorData: [],
	importSuccessData: [],
	importExpenseReportErrorData: [],
	importExpenseReportSuccessData: [],
	formData: null,
	currentPurchaseInfo: null,
	currentPreparePurchase: null,
	fetchPurchasesApiStatus: 'idle',
	fetchCurrentPurchaseApiStatus: 'idle',
	fetchCurrentPreparePurchaseApiStatus: 'idle',
	fetchAllPreparePurchasesApiStatus: 'idle',
	fetchReviewAndExportPreparePurchasesApiStatus: 'idle',
	fetchUnpaidExportPurchasesApiStatus: 'idle',
	createPurchaseApiStatus: 'idle',
	createPreparePurchaseApiStatus: 'idle',
	createOverheadApiStatus: 'idle',
	updatePurchaseApiStatus: 'idle',
	updatePaymentApiStatus: 'idle',
	dailyUpdatePaymentApiStatus: 'idle',
	updateArchiveApiStatus: 'idle',
	uploadInvoiceApiStatus: 'idle',
	updateToggleArchiveApiStatus: 'idle',
	updateMoveToNextApiStatus: 'idle',
	updateLastExportedDateApiStatus: 'idle',
	updateAssignApiStatus: 'idle',
	updateAssignDirectlyToExportsApiStatus: 'idle',
	updateMoveToPrevApiStatus: 'idle',
	updateMoveBackToReviewApiStatus: 'idle',
	editInvoiceApiStatus: 'idle',
	editPrepareInvoiceApiStatus: 'idle',
	deletePurchaseInvoiceApiStatus: 'idle',
	moveToPrepareFromExportApiStatus: 'idle',
	markAsToPayApiStatus: 'idle',
	bulkUndoPendingReconApiStatus: 'idle',
	bulkUndoToPayApiStatus: 'idle',
	undoToPayApiStatus: 'idle',
	bulkMarkAsPaidApiStatus: 'idle',
	bulkUnmarkAsPaidApiStatus: 'idle',
	importExportInvoicesInvoiceApiStatus: 'idle',
	importExpenseReportsInvoiceApiStatus: 'idle',
	generateBridgePaymentLinkApiStatus: 'idle',
	updatePaymentStatusAfterBridgePaymentLinkApiStatus: 'idle',
	changeCurrentPreparePurchaseInvoiceApiStatus: 'idle',
	toggleSupplierInvoicesFlagInvoiceApiStatus: 'idle',
	purchaseGridLoading: false,
	addInvoiceModalOpenStatus: false,
	newInvoiceModalOpenStatus: false,
	assignInvoiceModalOpenStatus: false,
	assignDirectlyToExportsInvoiceModalOpenStatus: false,
	confirmInvoiceModalOpenStatus: false,
	moveToPrepareModalOpenStatus: false,
	moveToReviewOpenStatus: false,
	addTagsModalOpenStatus: false,
	addEmailInvoiceModalOpenStatus: false,
	editInvoiceModalOpenStatus: false,
	editPrepareInvoiceModalOpenStatus: false,
	deleteInvoiceDialogOpenStatus: false,
	purchasePaidConfirmationDialogOpenStatus: false,
	purchaseUnpaidConfirmationDialogOpenStatus: false,
	purchaseArchivedConfirmationDialogOpenStatus: false,
	purchaseDownloadConfirmationDialogOpenStatus: false,
	prepareInvoiceArchiveConfirmationDialogOpenStatus: false,
	reviewInvoiceNoCostlineConfirmationDialogOpenStatus: false,
	supplierRedirectConfirmationDialogOpenStatus: false,
	removeEmailInvoiceDialogStatus: false,
	confirmSelectAllInExportDialogOpenStatus: false,
	confirmExportToPayPreparePurchasesDialogOpenStatus: false,
	confirmExportPreparePurchaseXMLDialogOpenStatus: false,
	confirmUnmarkToPayDialogOpenStatus: false,
	confirmExportDialogOpenStatus: false,
	importExportInvoicesAddSuppliersModalOpen: false,
	importExportInvoicesMessageDialogOpen: false,
	importExpenseReportsMessageDialogOpen: false,
	importExportInvoicesModalOpen: false,
	isBulkAction: false,
	importExpenseReportsModalOpen: false,
	bridgePaymentLinkDialogOpenStatus: false,
	error: null,
}

const throwPurchasesError = (
	message: string,
	context: Record<string, unknown> = {}
): never => {
	return captureExceptionAndThrow(new Error(message), {
		location: 'purchasesSlice',
		...context,
	})
}

const throwPurchasesStatusError = (
	status: string,
	context: Record<string, unknown> = {}
): never => {
	return throwPurchasesError(status || 'Unknown purchases error', {
		status,
		...context,
	})
}

export const fetchCurrentPurchase = createAsyncThunk<
	ReduxPurchase,
	{ purchaseId: string },
	{
		rejectValue: string
	}
>(
	'purchases/fetchCurrentPurchase',
	async ({ purchaseId }, { rejectWithValue }) => {
		try {
			const {
				data: { getPurchase },
			} = (await API.graphql(
				graphqlOperation(queries.getPurchase, {
					id: purchaseId,
				})
			)) as {
				data: { getPurchase: ReduxPurchase }
			}
			return getPurchase
		} catch (err: any) {
			console.error('fetchCurrentPurchase thunk input', { purchaseId })
			console.error('fetchCurrentPurchase thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchCurrentPreparePurchase = createAsyncThunk<
	ReduxPreparePurchase,
	{ preparePurchaseId: string },
	{
		rejectValue: string
	}
>(
	'purchases/fetchCurrentPreparePurchase',
	async ({ preparePurchaseId }, { rejectWithValue }) => {
		try {
			const {
				data: { getPreparePurchase },
			} = (await API.graphql(
				graphqlOperation(queries.getPreparePurchase, {
					id: preparePurchaseId,
				})
			)) as {
				data: { getPreparePurchase: ReduxPreparePurchase }
			}
			return getPreparePurchase
		} catch (err: any) {
			console.error('fetchCurrentPreparePurchase thunk input', {
				preparePurchaseId,
			})
			console.error('fetchCurrentPreparePurchase thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchPurchases = createAsyncThunk<
	{
		sortedItems: ReduxPurchase[]
		quoteData: ReduxQuote[]
		projectData: ReduxProjects[]
	},
	{ startDate?: any; endDate?: any },
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/fetchPurchases',
	async ({ startDate, endDate }, { getState, rejectWithValue }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const isFreelancer = userTableInfo.cognitoGroup?.includes('freelancer')
		const isTeamAdmin = userTableInfo.cognitoGroup?.includes('teamAdmin')
		const isSubTeamAdmin =
			userTableInfo.cognitoGroup?.includes('subTeamAdmin')
		const freelancerTenantId = userTableInfo.tenantId
		const teamIds = userTableInfo.teamIds
		const subTeamIds = userTableInfo.subTeamIds
		try {
			const tenantId = userTableInfo.currentCompanyTenantId
			const purchasesDataPromise = fetchPurchasesByTenantIdUtil({
				tenantId,
				startDate,
				endDate,
			})
			const suppliersDataPromise = fetchSuppliersByTenantIdUtil(tenantId)
			let quoteDataPromise: Promise<any[]>
			let projectDataPromise: Promise<any[]>
			if (isFreelancer) {
				quoteDataPromise = fetchChildFreelancerQuotes({
					tenantId,
					freelancerTenantId,
				})
				projectDataPromise = fetchChildFreelancerProjectUtil({
					freelancerTenantId,
					companyTenantId: tenantId,
				})
			} else if (isTeamAdmin) {
				quoteDataPromise = fetchAllQuotesByTenantIdUtilForTeamAdmin({
					tenantId,
					teamIds,
					startDate,
					endDate,
				})
				projectDataPromise = fetchProjectsByTenantIdUtilForTeamAdmin({
					tenantId,
					teamIds,
				})
			} else if (isSubTeamAdmin) {
				quoteDataPromise = fetchAllQuotesByTenantIdUtilForSubTeamAdmin({
					tenantId,
					subTeamIds,
					startDate,
					endDate,
				})
				projectDataPromise = fetchProjectsByTenantIdUtilForSubTeamAdmin(
					{
						tenantId,
						subTeamIds,
					}
				)
			} else {
				quoteDataPromise = fetchQuotesByTenantIdUtil(tenantId)

				projectDataPromise = fetchProjectsByTenantIdUtil(tenantId)
			}

			const [quoteData, projectData, purchaseData, suppliersData] =
				await Promise.all([
					quoteDataPromise,
					projectDataPromise,
					purchasesDataPromise,
					suppliersDataPromise,
				])
			const updatedPurchaseData = purchaseData.map((i) => {
				let newRow = {} as ReduxPurchase & {
					fullPurchaseNumber: string
				}
				const currentSupplierData = suppliersData.filter(
					(sd) => sd.id === i.supplierId
				)
				const currentSupplierName =
					currentSupplierData.length > 0
						? currentSupplierData[0].name
						: i.supplierName || 'Deleted'
				newRow.supplierName = currentSupplierName
				newRow.fullPurchaseNumber = `${currentSupplierName?.substring(0, 3)}-${
					i?.quoteNumber
				}-${padStart(i?.purchaseNumber ?? '', 4, '0')}`

				if (
					i.purchaseType === 'costline' ||
					i.purchaseType === 'newCostline'
				) {
					const currentQuoteData = quoteData.filter(
						(qd) => qd.id === i.quoteId
					)
					const currentQuoteName =
						currentQuoteData.length > 0
							? currentQuoteData[0].name
							: i.quoteName || 'Deleted'
					const currentProjectID = currentQuoteData.length
						? currentQuoteData[0].parentProjectId
						: null
					const currentProjectData = currentProjectID
						? projectData.filter((pd) => pd.id === currentProjectID)
						: null

					const currentProjectName =
						currentProjectData && currentProjectData.length > 0
							? currentProjectData[0].name
							: i.projectName || 'Deleted'
					newRow.quoteName = currentQuoteName
					newRow.projectName = currentProjectName
				} else {
					newRow.recapCostlineItem = ''
					newRow.quoteName = ''
					if (newRow.purchaseType === 'overhead') {
						newRow.projectName = 'Overhead'
					}
				}
				newRow = { ...i, ...newRow }
				return newRow
			})

			const sortedItems = orderBy(
				updatedPurchaseData,
				['invoiceId'],
				['asc']
			)

			return { sortedItems, quoteData, projectData }
		} catch (err: any) {
			console.log('err: ', err)
			console.error('fetchPurchases thunk input', { startDate, endDate })
			console.error('fetchPurchases thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchAllPreparePurchases = createAsyncThunk<
	ReduxPreparePurchase[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/fetchAllPreparePurchases',
	async (arg, { getState, rejectWithValue }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		try {
			const tenantId = userTableInfo.currentCompanyTenantId
			console.log('check company tenantId: ', tenantId)

			const preparePurchasesData =
				await fetchAllPrepareInvoicesByTenantIdUtil(tenantId)

			return preparePurchasesData
		} catch (err: any) {
			console.error('fetchAllPreparePurchases thunk input', {})
			console.error('fetchAllPreparePurchases thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchReviewAndExportPreparePurchases = createAsyncThunk<
	ReduxPreparePurchase[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/fetchReviewAndExportPreparePurchases',
	async (arg, { getState, rejectWithValue }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		try {
			const tenantId = userTableInfo.currentCompanyTenantId
			console.log('check company tenantId: ', tenantId)

			const preparePurchasesData =
				await fetchReviewAndExportPrepareInvoicesByTenantIdUtil(
					tenantId
				)

			return preparePurchasesData
		} catch (err: any) {
			console.error(
				'fetchReviewAndExportPreparePurchases thunk input',
				{}
			)
			console.error(
				'fetchReviewAndExportPreparePurchases thunk error',
				err
			)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const fetchUnpaidExportPurchases = createAsyncThunk<
	ReduxPreparePurchase[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/fetchUnpaidExportPurchases',
	async (arg, { getState, rejectWithValue }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		try {
			const tenantId = userTableInfo.currentCompanyTenantId
			console.log('check company tenantId: ', tenantId)

			const preparePurchasesData =
				await fetchUnpaidExportPurchasesByTenantIdUtil(tenantId)

			return preparePurchasesData
		} catch (err: any) {
			console.error('fetchUnpaidExportPurchases thunk input', {})
			console.error('fetchUnpaidExportPurchases thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchPurchasesFreelancer = createAsyncThunk<
	{
		sortedItems: ReduxPurchase[]
		quoteData: ReduxQuote[]
		projectData: ReduxProjects[]
	},
	{ freelancerTenantId: string; startDate?: any; endDate?: any },
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/fetchPurchasesFreelancer',
	async (
		{ freelancerTenantId, startDate, endDate },
		{ getState, rejectWithValue }
	) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		try {
			const tenantId = userTableInfo.currentCompanyTenantId
			const purchasesDataPromise = fetchPurchasesByTenantIdUtilFreelancer(
				{
					tenantId,
					freelancerTenantId,
					startDate,
					endDate,
				}
			)
			const suppliersDataPromise = fetchSuppliersByTenantIdUtil(tenantId)

			const quoteDataPromise = fetchQuotesByTenantIdUtil(tenantId)

			const projectDataPromise = fetchProjectsByTenantIdUtil(tenantId)

			const [quoteData, projectData, purchaseData, suppliersData] =
				await Promise.all([
					quoteDataPromise,
					projectDataPromise,
					purchasesDataPromise,
					suppliersDataPromise,
				])
			const updatedPurchaseData = purchaseData.map((i) => {
				let newRow = {} as ReduxPurchase & {
					fullPurchaseNumber: string
				}
				const currentSupplierData = suppliersData.filter(
					(sd) => sd.id === i.supplierId
				)

				const currentSupplierName =
					currentSupplierData.length > 0
						? currentSupplierData[0].name
						: i.supplierName || 'Deleted'
				newRow.supplierName = currentSupplierName
				newRow.fullPurchaseNumber = `${currentSupplierName?.substring(0, 3)}-${
					i?.quoteNumber
				}-${padStart(i?.purchaseNumber ?? '', 4, '0')}`
				if (
					i.purchaseType === 'costline' ||
					i.purchaseType === 'newCostline'
				) {
					const currentQuoteData = quoteData.filter(
						(qd) => qd.id === i.quoteId
					)
					const currentQuoteName =
						currentQuoteData.length > 0
							? currentQuoteData[0].name
							: i.quoteName || 'Deleted'
					const currentProjectID = currentQuoteData.length
						? currentQuoteData[0].parentProjectId
						: null
					const currentProjectData = currentProjectID
						? projectData.filter((pd) => pd.id === currentProjectID)
						: null
					const currentProjectName = currentProjectData
						? currentProjectData[0].name
						: i.projectName || 'Deleted'
					newRow.quoteName = currentQuoteName
					newRow.projectName = currentProjectName
				} else {
					newRow.recapCostlineItem = ''
					newRow.quoteName = ''
					if (i.purchaseType === 'overhead') {
						newRow.projectName = 'Overhead'
					}
				}
				newRow = { ...i, ...newRow }
				return newRow
			})

			const sortedItems = orderBy(
				updatedPurchaseData,
				['invoiceId'],
				['asc']
			)

			return { sortedItems, quoteData, projectData }
		} catch (err: any) {
			console.error('fetchPurchasesFreelancer thunk input', {
				freelancerTenantId,
				startDate,
				endDate,
			})
			console.error('fetchPurchasesFreelancer thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const moveToNext = createAsyncThunk<
	void,
	{ prepareInvoiceId: string },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/moveToNext',
	async ({ prepareInvoiceId }, { getState, rejectWithValue, dispatch }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const {
				data: { getPreparePurchase: items },
			} = (await API.graphql(
				graphqlOperation(queries.getPreparePurchase, {
					id: prepareInvoiceId,
				})
			)) as { data: { getPreparePurchase: ReduxPreparePurchase } }

			let nextPage = ''
			if (items.currentPage === 'prepare') {
				nextPage = 'review'
			} else if (items.currentPage === 'review') {
				nextPage = 'export'
			}

			const updateObject = {
				id: prepareInvoiceId,
				currentPage: nextPage,
				prevPage: items.currentPage,
			}

			if (!items.currentPage) {
				throwPurchasesError(
					`Prepare Purchase current page is null or undefined items.currentPage: ${items.currentPage}`,
					{
						action: 'moveToNext',
						prepareInvoiceId,
						currentPage: items.currentPage,
						userId: userTableInfo.id,
					}
				)
			}
			const {
				data: { updatePreparePurchase },
			} = (await API.graphql(
				graphqlOperation(mutations.updatePreparePurchase, {
					input: updateObject,
				})
			)) as { data: { updatePreparePurchase: ReduxPreparePurchase } }

			const logInput = {
				id: nanoid(),
				activeFlag: true,
				tenantId: userTableInfo.currentCompanyTenantId,
				name: userTableInfo.email,
				exportDate: dayjs().toISOString(),
				exportType: `Confirmed invoice ${
					updatePreparePurchase?.invoiceName ?? ''
				}`,
				loggedLineId: updatePreparePurchase?.id,
			}
			const tPurchases = i18next.getFixedT(null, 'purchases')
			const {
				data: { createExportInfo },
			} = (await API.graphql(
				graphqlOperation(mutations.createExportInfo, {
					input: logInput,
				})
			)) as { data: { createExportInfo: ReduxExportInfo } }

			dispatch(fetchReviewAndExportPreparePurchases())
			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)

			makeSuccessToast(
				`${tPurchases('toasts.movedTo')} ${tPurchases(
					`toasts.${nextPage}`
				)} `
			)
		} catch (err: any) {
			console.error('moveToNext thunk input', { prepareInvoiceId })
			console.error('moveToNext thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const updateLastExportedDate = createAsyncThunk<
	void,
	{ purchaseIds: string[] },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'purchases/updateLastExportedDate',
	async ({ purchaseIds }, { getState, rejectWithValue, dispatch }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			if (!purchaseIds || purchaseIds.length === 0) return
			const {
				data: {
					updateLastExportedDate: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.updateLastExportedDate, {
					companyTenantId: tenantId,
					preparePurchaseIdArray: purchaseIds,
				})
			)) as {
				data: {
					updateLastExportedDate: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'updateLastExportedDate',
					companyTenantId: tenantId,
					purchaseCount: purchaseIds.length,
				})
			}
		} catch (err: any) {
			const errorMessage = getErrorMessage(err)
			console.error(
				'errorMessage for updateLastExportedDate: ',
				errorMessage
			)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const moveToPrev = createAsyncThunk<
	void,
	{ preparePurchaseId: string },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'purchases/moveToPrev',
	async ({ preparePurchaseId }, { rejectWithValue, dispatch }) => {
		try {
			const tPurchases = i18next.getFixedT(null, 'purchases')
			const {
				data: { getPreparePurchase: items },
			} = (await API.graphql(
				graphqlOperation(queries.getPreparePurchase, {
					id: preparePurchaseId,
				})
			)) as { data: { getPreparePurchase: ReduxPreparePurchase } }

			let prevPage = ''
			if (items.currentPage === 'export') {
				prevPage = 'review'
			} else if (items.currentPage === 'review') {
				prevPage = 'prepare'
			}

			const updateObject = {
				id: preparePurchaseId,
				currentPage: prevPage,
			}
			if (!items.currentPage) {
				throwPurchasesError(
					`Prepare Purchase current page is null or undefined items.currentPage: ${items.currentPage}`,
					{
						action: 'moveToPrev',
						prepareInvoiceId: preparePurchaseId,
						currentPage: items.currentPage,
					}
				)
			}

			await API.graphql(
				graphqlOperation(mutations.updatePreparePurchase, {
					input: updateObject,
				})
			)

			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)

			makeSuccessToast(`${tPurchases('toasts.movedTo')} ${prevPage} `)
		} catch (err: any) {
			console.error('moveToPrev thunk input', { preparePurchaseId })
			console.error('moveToPrev thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const toggleArchive = createAsyncThunk<
	void,
	{ prepareInvoiceId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'purchases/toggleArchive',
	async ({ prepareInvoiceId }, { getState, rejectWithValue, dispatch }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		try {
			const {
				data: {
					toggleArchivePreparePurchase: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.toggleArchivePreparePurchase, {
					companyTenantId: tenantId,
					preparePurchaseId: prepareInvoiceId,
				})
			)) as {
				data: {
					toggleArchivePreparePurchase: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(
					`${tPurchases('toasts.movedTo')} ${tPurchases(
						`toasts.${status}`
					)} `
				)
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'toggleArchive',
					companyTenantId: tenantId,
					prepareInvoiceId,
				})
			}
		} catch (err: any) {
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const handleMoveToPrepareFromExport = createAsyncThunk<
	void,
	{ prepareInvoiceId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'purchases/handleMoveToPrepareFromExport',
	async ({ prepareInvoiceId }, { getState, rejectWithValue, dispatch }) => {
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			const {
				data: {
					handleMoveToPrepareFromExport: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.handleMoveToPrepareFromExport, {
					companyTenantId: tenantId,
					preparePurchaseId: prepareInvoiceId,
				})
			)) as {
				data: {
					handleMoveToPrepareFromExport: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.movedToPrepare'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'handleMoveToPrepareFromExport',
					companyTenantId: tenantId,
					prepareInvoiceId,
				})
			}
		} catch (err: any) {
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

type InvoiceDetailsType = {
	netAmountDetails: any
	supplierId: string
	taxAmountDetails: any
	totalAmount: number
}

export const assign = createAsyncThunk<
	void,
	{
		// Prepare purchase id
		prepareInvoiceId: string
		invoiceDetails: InvoiceDetailsType
		operation: 'existingCostlines' | 'overhead' | 'newCostline'
		// only needed in newCostline
		quoteIdArray?: string[]
		page?: string
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'purchases/assign',
	async (
		{ prepareInvoiceId, invoiceDetails, operation, quoteIdArray, page },
		{ getState, rejectWithValue, dispatch }
	) => {
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			if (page === 'prepare') {
				const {
					data: { getPreparePurchase },
				} = (await API.graphql(
					graphqlOperation(queries.getPreparePurchase, {
						id: prepareInvoiceId,
					})
				)) as { data: { getPreparePurchase: ReduxPreparePurchase } }
				const logInput = {
					id: nanoid(),
					activeFlag: true,
					tenantId: userTableInfo.currentCompanyTenantId,
					name: userTableInfo.email,
					exportDate: dayjs().toISOString(),
					exportType: `Confirmed invoice ${
						getPreparePurchase?.invoiceName ?? ''
					}`,
					loggedLineId: getPreparePurchase?.id,
				}
				const {
					data: { createExportInfo },
				} = (await API.graphql(
					graphqlOperation(mutations.createExportInfo, {
						input: logInput,
					})
				)) as { data: { createExportInfo: ReduxExportInfo } }
			}
			const tReviewInvoices = i18next.getFixedT(null, 'reviewInvoices')
			const {
				data: {
					assign: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.assign, {
					companyTenantId: tenantId,
					preparePurchaseId: prepareInvoiceId,
					invoiceDetails,
					operation,
					quoteIdArray,
				})
			)) as {
				data: { assign: { status: string; isSuccessful: boolean } }
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tReviewInvoices('toasts.assigned'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'assign',
					companyTenantId: tenantId,
					prepareInvoiceId,
					operation,
					invoiceDetailCount:
						invoiceDetails?.netAmountDetails?.length,
				})
			}
		} catch (err: any) {
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for assign: ', errorMessage)
			makeErrorToast(`${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const assignDirectlyToExports = createAsyncThunk<
	void,
	{
		prepareInvoiceId: string
		invoiceDetails: InvoiceDetailsType
		operation: 'overhead'
	},
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'purchases/assignDirectlyToExports',
	async (
		{ prepareInvoiceId, invoiceDetails, operation },
		{ getState, rejectWithValue, dispatch }
	) => {
		const tReviewInvoices = i18next.getFixedT(null, 'reviewInvoices')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			const {
				data: {
					assignDirectlyToExports: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.assignDirectlyToExports, {
					companyTenantId: tenantId,
					preparePurchaseId: prepareInvoiceId,
					operation,
				})
			)) as {
				data: {
					assignDirectlyToExports: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				dispatch(fetchReviewAndExportPreparePurchases())
				makeSuccessToast(tReviewInvoices('toasts.assigned'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'assignDirectlyToExports',
					companyTenantId: tenantId,
					prepareInvoiceId,
					operation,
				})
			}
		} catch (err: any) {
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const moveBackToReview = createAsyncThunk<
	void,
	{ preparePurchaseId: string },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'purchases/moveBackToReview',
	async ({ preparePurchaseId }, { getState, rejectWithValue, dispatch }) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		try {
			const {
				data: {
					moveBackToReview: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.moveBackToReview, {
					companyTenantId: tenantId,
					preparePurchaseId: preparePurchaseId,
				})
			)) as {
				data: {
					moveBackToReview: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.movedToReview'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'moveBackToReview',
					companyTenantId: tenantId,
					prepareInvoiceId: preparePurchaseId,
				})
			}
		} catch (err: any) {
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for moveBackToReview: ', errorMessage)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const createPurchases = createAsyncThunk<
	void,
	{ purchaseData: Partial<ReduxPurchase> },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'purchases/createPurchases',
	async ({ purchaseData }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userTableInfo.currentCompanyTenantId,
				})
			)) as { data: { getCompany: ReduxCompany } }
			const {
				data: { getQuote: quoteInfo },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, {
					id: purchaseData.quoteId,
				})
			)) as { data: { getQuote: ReduxQuote } }

			const {
				data: { getProjects: projectInfo },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: quoteInfo.parentProjectId,
				})
			)) as { data: { getProjects: ReduxProjects } }

			const currPurchaseCount = getCompany?.purchaseCount ?? 1
			const newPurchaseCount = currPurchaseCount + 1

			const newPurchase = {
				tenantId: userTableInfo.currentCompanyTenantId,
				activeFlag: true,
				poAvailable: false,
				invoiceAvailable: false,
				paymentStatus: 'Draft',
				purchaseType: 'costline',
				quoteName: quoteInfo.name,
				projectName: projectInfo.name,
				quoteNumber: quoteInfo.quoteNumber,
				projectNumber: projectInfo.projectNumber,
				purchaseNumber: currPurchaseCount,
				projectId: projectInfo.id,
				...purchaseData,
			}
			const updateCompany = {
				id: getCompany?.id,
				purchaseCount: newPurchaseCount,
			}
			const createdPurchase = await API.graphql(
				graphqlOperation(mutations.createPurchase, {
					input: newPurchase,
				})
			)
			console.log('createdPurchase: ', createdPurchase)
			const updatedCompany = await API.graphql(
				graphqlOperation(mutations.updateCompany, {
					input: updateCompany,
				})
			)
			console.log('updatedCompany: ', updatedCompany)
			// await Promise.allSettled([createPurchase, updatePurchase])

			dispatch(fetchPurchases({}))
		} catch (err: any) {
			console.error('createPurchases thunk input', { purchaseData })
			console.error('createPurchases thunk error', err)
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for createPurchases: ', errorMessage)
			makeErrorToast(msg)
			return rejectWithValue(errorMessage)
		}
	}
)

export const startPreparePurchaseOcr = createAsyncThunk<
	void,
	{
		preparePurchaseId: string
		fileUploadDetails: ReduxS3Object
		fileName: string
	},
	{ rejectValue: string }
>(
	'purchases/startPreparePurchaseOcr',
	async (
		{ preparePurchaseId, fileUploadDetails, fileName },
		{ rejectWithValue }
	) => {
		try {
			await API.graphql(
				graphqlOperation(mutations.preparePurchaseOcr, {
					bucket: fileUploadDetails.bucket,
					region: fileUploadDetails.region,
					key: `protected/${fileUploadDetails.identityId}/${fileUploadDetails.key}`,
					preparePurchaseId,
				})
			)
		} catch (err: any) {
			if (err.errors[0].errorType === 'ExecutionTimeout') {
				makeSuccessToast(
					`Ocr processing for ${fileName} please wait 2 mins and check`
				)
				return
			}
			console.error('startPreparePurchaseOcr thunk input', {
				preparePurchaseId,
				fileUploadDetails,
				fileName,
			})
			console.error('startPreparePurchaseOcr thunk error', err)
			const errorMessage = getErrorMessage(err)
			console.error(
				'errorMessage for startPreparePurchaseOcr: ',
				errorMessage
			)
			makeErrorToast(msg)
			return rejectWithValue(errorMessage)
		}
	}
)

interface InvoiceDetails extends Partial<ReduxPurchase> {
	suppliers?: string
	amount: string
	tax: string
	date?: Date
	costlines: string[]
}

export const updatePurchase = createAsyncThunk<
	void,
	{ purchaseData: Partial<ReduxPurchase> },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'purchases/updatePurchase',
	async ({ purchaseData }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const isFreelancer =
				userTableInfo.cognitoGroup?.includes('freelancer')
			const freelancerTenantId = userTableInfo.tenantId
			const {
				data: { getPurchase: items },
			} = (await API.graphql(
				graphqlOperation(queries.getPurchase, {
					id: purchaseData.id,
				})
			)) as { data: { getPurchase: ReduxPurchase } }

			const updatePurchase = getProperUpdateValues({
				updatedInput: purchaseData,
				originalInput: items,
			})

			if (hasBusinessChanges(updatePurchase)) {
				await API.graphql(
					graphqlOperation(mutations.updatePurchase, {
						input: updatePurchase,
					})
				)
			}
			if (!isFreelancer) {
				dispatch(fetchPurchases({}))
			} else if (freelancerTenantId && isFreelancer) {
				dispatch(fetchPurchasesFreelancer({ freelancerTenantId }))
			}
		} catch (err: any) {
			console.error('updatePurchase thunk input', { purchaseData })
			console.error('updatePurchase thunk error', err)
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for updatePurchase: ', errorMessage)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// *******************************************************************************
export const updatePurchaseTags = createAsyncThunk<
	void,
	{
		purchaseId: string
		tagNames: string[]
		newCompanyTagsArray: { title: string; color: string }[]
	},
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'purchases/updatePurchaseTags',
	async (
		{ purchaseId, tagNames, newCompanyTagsArray },
		{ getState, rejectWithValue, dispatch }
	) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const isFreelancer = userTableInfo.cognitoGroup?.includes('freelancer')
		const freelancerTenantId = userTableInfo.tenantId
		try {
			const {
				data: { getPurchase: items },
			} = (await API.graphql(
				graphqlOperation(queries.getPurchase, {
					id: purchaseId,
				})
			)) as { data: { getPurchase: ReduxPurchase } }

			const oldPurchaseTags = items.tags
			const updatePurchase: Partial<ReduxPurchase> = {
				id: purchaseId,
			}

			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: userTableInfo.currentCompanyTenantId,
				})
			)) as { data: { getCompany: ReduxCompany } }

			const updateCompanyDetails: Partial<ReduxCompany> = {
				id: getCompany.id,
			}

			const oldCompanyTags = cloneDeep(getCompany.purchaseTags || [])

			if (
				!oldPurchaseTags ||
				!(oldPurchaseTags.length > 0) ||
				!(tagNames.length > 0) ||
				tagNames.length !== oldPurchaseTags.length ||
				(tagNames.length === oldPurchaseTags.length &&
					!tagNames.every((item: any) =>
						oldPurchaseTags.includes(item)
					) &&
					!oldPurchaseTags.every((item) =>
						tagNames.includes(item as string)
					))
			) {
				updatePurchase.tags = tagNames

				await API.graphql(
					graphqlOperation(mutations.updatePurchase, {
						input: updatePurchase,
					})
				)

				if (
					!oldCompanyTags ||
					!isEqual(oldCompanyTags, newCompanyTagsArray)
				) {
					if (!oldCompanyTags) {
						updateCompanyDetails.purchaseTags = newCompanyTagsArray
					} else {
						if (newCompanyTagsArray.length > 0) {
							updateCompanyDetails.purchaseTags = [
								...oldCompanyTags,
								...newCompanyTagsArray,
							]
						}
					}
				}
				if (hasBusinessChanges(updateCompanyDetails)) {
					await updateCompanyInfoWithDeltaSyncSuppress(
						updateCompanyDetails
					)
					dispatch(
						companyApi.util.invalidateTags([{ type: 'Company' }])
					)
				}
				if (!isFreelancer) {
					dispatch(fetchPurchases({}))
				} else if (freelancerTenantId && isFreelancer) {
					dispatch(fetchPurchasesFreelancer({ freelancerTenantId }))
				}
				makeSuccessToast(tPurchases('toasts.tagsUpdated'))
			}
		} catch (err: any) {
			console.error('updatePurchaseTags thunk input', {
				purchaseId,
				tagNames,
				newCompanyTagsArray,
			})
			console.error('updatePurchaseTags thunk error', err)
			const errorMessage = getErrorMessage(err)
			console.error('errorMessage for updatePurchaseTags: ', errorMessage)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
// *******************************************************************************
// ***********************************Toggle Paid & unpaid********************************************
export const updatePaymentStatus = createAsyncThunk<
	void,
	{ date?: Date; currentPreparePurchaseId: string },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/updatePaymentStatus',
	async (
		{ date, currentPreparePurchaseId },
		{ getState, dispatch, rejectWithValue }
	) => {
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			const tInvoices = i18next.getFixedT(null, 'invoices')
			const {
				data: {
					updatePaymentStatus: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.updatePaymentStatus, {
					companyTenantId: tenantId,
					preparePurchaseId: currentPreparePurchaseId,
					date: date,
				})
			)) as {
				data: {
					updatePaymentStatus: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				status === 'Paid'
					? makeSuccessToast(tInvoices('toasts.markPaid'))
					: makeSuccessToast(tInvoices('toasts.markUnpaid'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'updatePaymentStatus',
					companyTenantId: tenantId,
					prepareInvoiceId: currentPreparePurchaseId,
					date,
				})
			}
		} catch (err: any) {
			const errorMessage = getErrorMessage(err)
			console.error(
				'errorMessage for updatePaymentStatus: ',
				errorMessage
			)
			makeErrorToast(` ${errorMessage}`)
			return rejectWithValue(errorMessage)
		}
	}
)

// *******************************************************************************

// *******************************************************************************
export const dailyUpdatePaymentStatus = createAsyncThunk<
	void,
	{ preparePurchaseIdArray: string[] },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/dailyUpdatePaymentStatus',
	async (
		{ preparePurchaseIdArray },
		{ getState, dispatch, rejectWithValue }
	) => {
		console.log('preparePurchaseIdArray: ', preparePurchaseIdArray)
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const filter = getFetchFilter(userTableInfo)
		let errorMessage = msg
		try {
			const {
				data: {
					dailyUpdatePaymentStatusFromPreparePurchase: {
						status,
						isSuccessful,
					},
				},
			} = (await API.graphql(
				graphqlOperation(
					mutations.dailyUpdatePaymentStatusFromPreparePurchase,
					{
						companyTenantId: tenantId,
						preparePurchaseIdArray: preparePurchaseIdArray,
					}
				)
			)) as {
				data: {
					dailyUpdatePaymentStatusFromPreparePurchase: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (!isSuccessful) {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'dailyUpdatePaymentStatusFromPreparePurchase',
					companyTenantId: tenantId,
					preparePurchaseCount: preparePurchaseIdArray.length,
				})
			}
			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)
		} catch (err: any) {
			makeErrorToast(
				`Error (automatically updating payment status): ${errorMessage}`
			)
			return rejectWithValue(err)
		}
	}
)

// *******************************************************************************
// *******************************************************************************
export const dailyUpdatePaymentStatusFromPurchases = createAsyncThunk<
	void,
	{ currentPurchase: ReduxPurchase },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'purchases/dailyUpdatePaymentStatusFromPurchases',
	async ({ currentPurchase }, { dispatch, getState, rejectWithValue }) => {
		let errorMessage = msg
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const isFreelancer = userTableInfo.cognitoGroup?.includes('freelancer')
		const freelancerTenantId = userTableInfo.tenantId
		try {
			if (!currentPurchase || !currentPurchase?.id) {
				throwPurchasesError('Purchase or purchase id is missing', {
					action: 'dailyUpdatePaymentStatusFromPurchases',
					purchaseId: currentPurchase?.id,
				})
			}
			const {
				data: {
					dailyUpdatePaymentStatusFromPurchases: {
						status,
						isSuccessful,
					},
				},
			} = (await API.graphql(
				graphqlOperation(
					mutations.dailyUpdatePaymentStatusFromPurchases,
					{
						companyTenantId: tenantId,
						purchaseId: currentPurchase?.id,
					}
				)
			)) as {
				data: {
					dailyUpdatePaymentStatusFromPurchases: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				if (!isFreelancer) {
					dispatch(fetchPurchases({}))
				} else if (freelancerTenantId && isFreelancer) {
					dispatch(fetchPurchasesFreelancer({ freelancerTenantId }))
				}
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'dailyUpdatePaymentStatusFromPurchases',
					companyTenantId: tenantId,
					purchaseId: currentPurchase?.id,
				})
			}
		} catch (err: any) {
			makeErrorToast(
				`Error (automatically updating payment status): ${errorMessage}`
			)
			return rejectWithValue(err)
		}
	}
)

// *******************************************************************************

// *******************************************************************************

export const editPreparePurchase = createAsyncThunk<
	void,
	{ preparePurchaseDetails: Partial<ReduxPreparePurchase>; type?: string },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/editPreparePurchase',
	async (
		{ preparePurchaseDetails, type },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tExportInvoices = i18next.getFixedT(null, 'exportInvoices')
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const {
				data: { getPreparePurchase },
			} = (await API.graphql(
				graphqlOperation(queries.getPreparePurchase, {
					id: preparePurchaseDetails.id,
				})
			)) as { data: { getPreparePurchase: ReduxPreparePurchase } }

			let updateQuoteNumber = ''
			if (
				preparePurchaseDetails.netAmountDetails &&
				preparePurchaseDetails.netAmountDetails.length > 0
			) {
				const allNetAmtDetails = preparePurchaseDetails.netAmountDetails

				for (const eachNetAmtDetails of allNetAmtDetails) {
					if (eachNetAmtDetails.costlineId) {
						console.log('eachNetAmtDetails: ', eachNetAmtDetails)
						const {
							data: { getRecapInfo: costline },
						} = (await API.graphql(
							graphqlOperation(queries.getRecapInfo, {
								id: eachNetAmtDetails.costlineId,
							})
						)) as {
							data: { getRecapInfo: ReduxRecapInfo }
						}
						const {
							data: { getQuote: quoteDetails },
						} = (await API.graphql(
							graphqlOperation(queries.getQuote, {
								id: costline?.quoteId,
							})
						)) as { data: { getQuote: ReduxQuote } }
						updateQuoteNumber += `${quoteDetails?.quoteNumber},`
						eachNetAmtDetails.costlineName = costline.item as any
						eachNetAmtDetails.costlineName_fr =
							costline.item_fr as any
					}
				}
				preparePurchaseDetails.quoteNumber = updateQuoteNumber.slice(
					0,
					updateQuoteNumber.length - 1
				)
			}

			const updatePreparePurchaseParams = getProperUpdateValues({
				updatedInput: preparePurchaseDetails,
				originalInput: getPreparePurchase,
			})
			console.log(
				'updatePreparePurchaseParams: ',
				updatePreparePurchaseParams
			)

			const {
				data: { updatePreparePurchase },
			} = (await API.graphql(
				graphqlOperation(mutations.updatePreparePurchase, {
					input: updatePreparePurchaseParams,
				})
			)) as { data: { updatePreparePurchase: ReduxPreparePurchase } }

			console.log('type: ', type)
			if (type === 'save') {
				const logInput = {
					id: nanoid(),
					activeFlag: true,
					tenantId: userTableInfo.currentCompanyTenantId,
					name: userTableInfo.email,
					exportDate: dayjs().toISOString(),
					exportType: `Saved invoice ${
						updatePreparePurchase?.invoiceName ?? ''
					}`,
					loggedLineId: updatePreparePurchase?.id,
				}
				const {
					data: { createExportInfo },
				} = (await API.graphql(
					graphqlOperation(mutations.createExportInfo, {
						input: logInput,
					})
				)) as { data: { createExportInfo: ReduxExportInfo } }
			}
			if (!getPreparePurchase.currentPage) {
				throwPurchasesError(
					`getPreparePurchase.currentPage: ${getPreparePurchase.currentPage}`,
					{
						action: 'updatePreparePurchase',
						preparePurchaseId: preparePurchaseDetails.id,
						currentPage: getPreparePurchase.currentPage,
					}
				)
			}
			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)
			makeSuccessToast(tExportInvoices('toasts.successfullyUpdated'))
		} catch (err: any) {
			console.error('editPreparePurchase thunk input', {
				preparePurchaseDetails,
				type,
			})
			console.error('editPreparePurchase thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// ! Take a deeper look to see if this is still used
export const deleteInvoice = createAsyncThunk<
	void,
	{ currentPurchaseId: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/deleteInvoice',
	async ({ currentPurchaseId }, { dispatch, rejectWithValue }) => {
		try {
			const {
				data: { getPurchase },
			} = (await API.graphql(
				graphqlOperation(queries.getPurchase, { id: currentPurchaseId })
			)) as {
				data: { getPurchase: ReduxPurchase }
			}
			const {
				data: { getSuppliers },
			} = (await API.graphql(
				graphqlOperation(queries.getSuppliers, {
					id: getPurchase.supplierId,
				})
			)) as {
				data: { getSuppliers: ReduxSuppliers }
			}

			const updatePurchase: Partial<ReduxPurchase> = {
				id: getPurchase.id,
				invoiceTotal: null,
				taxAmount: null,
				supplierCreditorAccount: getSuppliers.supplierCreditorAccount,
				purchaseInvoiceDueDate: null,
				paymentStatus: 'Draft',
				invoiceDocuments: [],
				invoiceAvailable: false,
				invoiceKey: null,
			}
			await API.graphql(
				graphqlOperation(mutations.updatePurchase, {
					input: updatePurchase,
				})
			)

			if (
				getPurchase.purchaseType === 'costline' ||
				getPurchase.purchaseType === 'newCostline'
			) {
				const {
					data: { getRecapInfo },
				} = (await API.graphql(
					graphqlOperation(queries.getRecapInfo, {
						id: getPurchase.recapCostlineId,
					})
				)) as { data: { getRecapInfo: ReduxRecapInfo } }

				const updateRecapInfo: Partial<ReduxRecapInfo> = {
					id: getRecapInfo.id,
					paymentStatus: 'Draft',
					purchaseTotal: null,
					invoiceAvailable: false,
				}
				await API.graphql(
					graphqlOperation(mutations.updateRecapInfo, {
						input: updateRecapInfo,
					})
				)
			}
			dispatch(fetchPurchases({}))
			makeSuccessToast('Deleted Invoice')
		} catch (err: any) {
			console.error('deleteInvoice thunk input', { currentPurchaseId })
			console.error('deleteInvoice thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const markAsToPay = createAsyncThunk<
	void,
	{ selected: string[] },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/markAsToPay',
	async ({ selected }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		const tPurchases = i18next.getFixedT(null, 'purchases')
		try {
			const {
				data: {
					markAsToPay: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.markAsToPay, {
					companyTenantId: tenantId,
					selected: selected,
				})
			)) as {
				data: {
					markAsToPay: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.markedAsToPay'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'markAsToPay',
					companyTenantId: tenantId,
					selectedCount: selected.length,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const bulkUndoToPay = createAsyncThunk<
	void,
	{ selected: string[] },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/bulkUndoToPay',
	async ({ selected }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			const {
				data: {
					bulkUndoToPay: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.bulkUndoToPay, {
					companyTenantId: tenantId,
					selected: selected,
				})
			)) as {
				data: {
					bulkUndoToPay: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.undoToPay'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'bulkUndoToPay',
					companyTenantId: tenantId,
					selectedCount: selected.length,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const undoToPay = createAsyncThunk<
	void,
	{ rowId: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/undoToPay',
	async ({ rowId }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			const {
				data: {
					undoToPay: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.undoToPay, {
					companyTenantId: tenantId,
					rowId: rowId,
				})
			)) as {
				data: {
					undoToPay: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.undoToPay'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'undoToPay',
					companyTenantId: tenantId,
					rowId,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const markAsPendingReconciliation = createAsyncThunk<
	void,
	{ preparePurchaseIdArray: string[] | null | undefined },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/markAsPendingReconciliation',
	async (
		{ preparePurchaseIdArray },
		{ dispatch, rejectWithValue, getState }
	) => {
		console.log('preparePurchaseIdArray: ', preparePurchaseIdArray)
		let errorMessage = msg
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			if (
				!preparePurchaseIdArray ||
				preparePurchaseIdArray.length === 0
			) {
				return
			}
			const {
				data: {
					markAsPendingReconciliation: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.markAsPendingReconciliation, {
					companyTenantId: tenantId,
					preparePurchaseIdArray: preparePurchaseIdArray,
				})
			)) as {
				data: {
					markAsPendingReconciliation: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'markAsPendingReconciliation',
					companyTenantId: tenantId,
					preparePurchaseCount: preparePurchaseIdArray.length,
				})
			}
		} catch (err: any) {
			console.error('markAsPendingReconciliation thunk input', {
				preparePurchaseIdArray,
			})
			console.error('markAsPendingReconciliation thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const bulkUndoPendingRecon = createAsyncThunk<
	void,
	{ selected: string[] },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/bulkUndoPendingRecon',
	async ({ selected }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			console.log('tenantId: ', tenantId)
			const {
				data: {
					bulkUndoPendingReconciliation: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.bulkUndoPendingReconciliation, {
					companyTenantId: tenantId,
					selected: selected,
				})
			)) as {
				data: {
					bulkUndoPendingReconciliation: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.bulkUndoPendingRecon'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'bulkUndoPendingReconciliation',
					companyTenantId: tenantId,
					selectedCount: selected.length,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const bulkMarkAsPaid = createAsyncThunk<
	void,
	{ selected: string[] },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/bulkMarkAsPaid',
	async ({ selected }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			console.log('selected: ', selected)
			const {
				data: {
					bulkMarkAsPaid: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.bulkMarkAsPaid, {
					companyTenantId: tenantId,
					selected: selected,
				})
			)) as {
				data: {
					bulkMarkAsPaid: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.markPaid'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'bulkMarkAsPaid',
					companyTenantId: tenantId,
					selectedCount: selected.length,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const bulkUnmarkAsPaid = createAsyncThunk<
	void,
	{ selected: string[] },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/bulkUnmarkAsPaid',
	async ({ selected }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const tPurchases = i18next.getFixedT(null, 'purchases')
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			console.log('selected: ', selected)
			const {
				data: {
					bulkUnmarkAsPaid: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.bulkUnmarkAsPaid, {
					companyTenantId: tenantId,
					selected: selected,
				})
			)) as {
				data: {
					bulkUnmarkAsPaid: { status: string; isSuccessful: boolean }
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				makeSuccessToast(tPurchases('toasts.markPaid'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'bulkUnmarkAsPaid',
					companyTenantId: tenantId,
					selectedCount: selected.length,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

const validateImportPurchasesData = async ({
	results,
	newSuppliersData,
	projectData,
	missingRequiredFields,
	noProjectWithProjectNumberFields,
	updatedPurchaseData,
	errorData,
}: {
	results: any
	newSuppliersData: ReduxSuppliers[]
	projectData: ReduxProjects[]
	missingRequiredFields: any
	noProjectWithProjectNumberFields: any
	updatedPurchaseData: any
	errorData: any
}) => {
	const tExportInvoices = i18next.getFixedT(null, 'exportInvoices')
	//Filter suppliers that have the required fields
	results?.map((d: any) => {
		const newObj = { ...d }
		const firstName = d.prenom.trim()
		const lastName = d.nom.trim()
		const fullName = `${firstName} ${lastName}`
		const getSupplier = newSuppliersData.find((s) => s.name === fullName)
		const getProject = projectData.find(
			(p) => p.projectNumber === d.axeAnalytique
		)

		const total = d['remunerationTotale€Brut']

		const invDate = d.dernierJourTravaille
		const totalWithDecimal = Number.parseFloat(total.replace(',', '.'))

		const payrollTax = Number.parseFloat(d.partPatronale.replace(',', '.'))

		newObj.invoiceDate = invDate
		if (getSupplier) {
			newObj.projectNumber = d.axeAnalytique
			newObj.total = totalWithDecimal
			newObj.payrollTax = payrollTax
			newObj.supplierId = getSupplier.id
			newObj.supplierName = getSupplier.name
			newObj.supplierType = getSupplier.supplierType
			newObj.supplierCreditorAccount = getSupplier.supplierCreditorAccount
		} else {
			newObj.projectNumber = d.axeAnalytique
			newObj.total = totalWithDecimal

			newObj.payrollTax = payrollTax
			newObj.supplierId = null
			newObj.supplierName = null
			newObj.supplierType = null
			newObj.supplierCreditorAccount = null
		}
		if (getProject) {
			newObj.projectName = getProject.name
			newObj.projectId = getProject.id
		} else {
			if (newObj.projectNumber.toLowerCase().trim() === 'overhead') {
				newObj.projectName = 'overhead'
				newObj.projectName = 'Overhead'
				newObj.projectId = 'overhead'
			} else {
				newObj.projectName = null
				newObj.projectId = null
			}
		}
		updatedPurchaseData.push(newObj)
		return d
	})

	//Filter suppliers that don't have the required fields
	missingRequiredFields = updatedPurchaseData?.filter(
		(d: any) => !d.supplierId || d.projectNumber.trim() === '' || !d.total
	)
	updatedPurchaseData = updatedPurchaseData?.filter(
		(d: any) => d.supplierId && d.projectNumber.trim() !== '' && d.total
	)

	noProjectWithProjectNumberFields = updatedPurchaseData?.filter(
		(d: any) => !d.projectName
	)
	updatedPurchaseData = updatedPurchaseData?.filter((d: any) => d.projectName)

	const projectNameArray = updatedPurchaseData.map((p: any) => {
		return p.projectName
	})
	const payrollArray = []
	for (const projectName of projectNameArray) {
		const filteredData: any = updatedPurchaseData.filter(
			(p: any) => p.projectName === projectName
		)
		if (filteredData.length > 0) {
			const newPayrollObj: any = { ...filteredData[0] }
			const total = filteredData.reduce(
				(a: any, b: any) => a + (b.payrollTax || 0),
				0
			)
			newPayrollObj.total = total
			newPayrollObj.invoiceDate = filteredData?.[0]?.invoiceDate
			newPayrollObj.payrollTax = ''
			newPayrollObj.supplierId = ''
			newPayrollObj.supplierName = 'Payroll'
			newPayrollObj.supplierType = ''
			newPayrollObj.supplierCreditorAccount = ''
			payrollArray.push(newPayrollObj)
		}
	}

	//Add statuses to each array, to show the use what went wrong

	if (missingRequiredFields.length > 0) {
		missingRequiredFields = missingRequiredFields.map((i: any) => {
			const updateObj = {
				...i,
				importStatus: `${tExportInvoices('import.missingField')}`,
			}
			errorData.push(updateObj)
			return updateObj
		})
	}
	if (noProjectWithProjectNumberFields.length > 0) {
		noProjectWithProjectNumberFields = noProjectWithProjectNumberFields.map(
			(i: any) => {
				const updateObj = {
					...i,
					importStatus: `${tExportInvoices(
						'import.noProjectWithProjectNumber'
					)}`,
				}
				errorData.push(updateObj)
				return updateObj
			}
		)
	}

	const finalData = [...updatedPurchaseData, ...payrollArray]
	return { successData: finalData, failedData: errorData }
}

export const importExportInvoices = createAsyncThunk<
	any,
	any | undefined,
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/importExportInvoices',
	async (suppliersName, { getState, dispatch, rejectWithValue }) => {
		const tExportInvoices = i18next.getFixedT(null, 'exportInvoices')
		try {
			const { auth, purchases } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			const results = purchases.importExportInvoicesResultsData
			if (suppliersName && suppliersName?.length > 0) {
				for (const s of suppliersName) {
					let newSupplier: any = {
						id: nanoid(),
						tenantId: userTableInfo.currentCompanyTenantId,
						activeFlag: true,
						currency: 'EUR',
						supplierCreditorAccount:
							'601 - Achats stockés - Matières premières (et fournitures)',
						paymentTerms: 'net30',
						taxType: 'TVA',
						taxRate: '20',
						name: s,
						supplierType: 'individual',
						individualType: 'Casual Entertainment Worker',
					}
					let newSubAccount

					if (s.length > 6) {
						newSubAccount = `${s.substring(0, 6)}`
					} else {
						newSubAccount = `${s}`.padEnd(6, '0')
					}

					newSupplier.supplierSubAccount = newSubAccount

					// Default quote categories
					newSupplier.apfpCategoryUuid = categories.find(
						(i) => i.supplierDefault
					)?.categoryUuid
					newSupplier.postProdCategoryUuid =
						postProductionCategories.find(
							(i) => i.supplierDefault
						)?.categoryUuid
					newSupplier.aicpSubCategoryUuid = aicpSubCategories.find(
						(i) => i.supplierDefault
					)?.subCategoryUuid
					newSupplier.photoSubCategoryUuid =
						photoGridSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.soundProdSubCategoryUuid =
						soundProdSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.cnc5chSubCategoryUuid =
						cnc5chSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteAnimationOnlySubCategoryUuid =
						quoteAnimationOnlySubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteAnimationAndLiveActionSubCategoryUuid =
						quoteAnimationAndLiveActionSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteDigitalSubCategoryUuid =
						quoteDigitalSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier = getSupplierUuids({
						newSupplierData: newSupplier,
					})
					console.log('newSupplier: ', newSupplier)

					await API.graphql(
						graphqlOperation(mutations.createSuppliers, {
							input: newSupplier,
						})
					)
				}
			}
			const newSuppliersData =
				await fetchSuppliersByTenantIdUtil(tenantId)
			const projectData = await fetchProjectsByTenantIdUtil(tenantId)
			const missingRequiredFields: any = []
			const noProjectWithProjectNumberFields: any = []
			const updatedPurchaseData: any = []
			const errorData: any = []
			const { successData, failedData } =
				await validateImportPurchasesData({
					results,
					newSuppliersData,
					projectData,
					missingRequiredFields,
					noProjectWithProjectNumberFields,
					updatedPurchaseData,
					errorData,
				})
			console.log('failedData: ', failedData)
			console.log('successData: ', successData)
			for (const data of successData) {
				let type = 'importedCostline'
				let supplierId = data.supplierId
				if (data.projectNumber === 'overhead') {
					type = 'importedOverhead'
				}
				if (data.supplierName === 'Payroll') {
					supplierId = 'payroll'
				}
				const newInvDate = data?.invoiceDate
					? dayjs(data?.invoiceDate, 'DD/MM/YYYY')
					: dayjs().toISOString()
				const newNetAmtDetails = {
					projectName: data.projectName,
					projectId: data.projectId,
					projectNumber: data.projectNumber,
					netAmount: data.total,
					category: data.supplierCreditorAccount,
				}
				const newPreparePurchase = {
					tenantId: userTableInfo.currentCompanyTenantId,
					activeFlag: true,
					// invoiceName: '????',
					invoiceDate: newInvDate,
					paymentStatus: 'Paid',
					preparePurchasePaidDate: newInvDate,
					type: type,
					netAmountDetails: newNetAmtDetails,
					taxAmountDetails: [
						{
							taxAmount: 0,
							vatAccount:
								'445660-TVA sur autres biens et services',
							vatIntracom: 'None',
						},
					],
					totalAmount: data.total,
					supplierId: supplierId,
					supplier: data.supplierName,

					currency: 'EUR',
					currentPage: 'export',
					lastExportedDate: dayjs().toISOString(),
				}
				console.log('newPreparePurchase: ', newPreparePurchase)
				await API.graphql(
					graphqlOperation(mutations.createPreparePurchase, {
						input: newPreparePurchase,
					})
				)
			}

			dispatch(fetchSuppliers())
			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)
			makeSuccessToast(
				tExportInvoices('import.importedExportInvoicesSuccessfully')
			)
			return { successData: successData, errorData: failedData }
		} catch (err: any) {
			console.error('importExportInvoices thunk input', { suppliersName })
			console.error('importExportInvoices thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

const validateImportExpenseReportsData = async ({
	results,
	newSuppliersData,
	projectData,
	missingRequiredFields,
	noProjectWithProjectNumberFields,
	suppliersNotIndividuals,
	updatedData,
	errorData,
}: {
	results: any
	newSuppliersData: ReduxSuppliers[]
	projectData: ReduxProjects[]
	missingRequiredFields: any
	noProjectWithProjectNumberFields: any
	suppliersNotIndividuals: any
	updatedData: any
	errorData: any
}) => {
	const tExportInvoices = i18next.getFixedT(null, 'exportInvoices')
	//Filter suppliers that have the required fields
	results?.map((d: any) => {
		const newObj = { ...d }
		const fullName = d.supplier.trim()
		const getSupplier = newSuppliersData.find(
			(s) => s.name?.trim() === fullName
		)
		const getProject = projectData.find(
			(p) => p.projectNumber === d.projectNumber
		)

		const total = d.totalAmount
		const netAmount = d.netAmount
		const taxAmount = d.taxAmount

		const invDate = d.invoiceDate
		const paymentDate = d.dueDate
		const paidDate = d.paidDate
		const totalWithDecimal = Number.parseFloat(total.replace(',', '.'))
		const netAmountWithDecimal = Number.parseFloat(
			netAmount.replace(',', '.')
		)
		const taxAmountWithDecimal = Number.parseFloat(
			taxAmount.replace(',', '.')
		)

		newObj.invoiceDate = invDate
		newObj.paymentDate = paymentDate
		newObj.paidDate = paidDate
		newObj.currency = d.currency ?? 'EUR'

		newObj.projectNumber = d.projectNumber
		newObj.total = totalWithDecimal
		newObj.netAmount = netAmountWithDecimal
		newObj.taxAmount = taxAmountWithDecimal
		if (getSupplier) {
			newObj.supplierId = getSupplier.id
			newObj.supplierName = getSupplier.name
			newObj.supplierType = getSupplier.supplierType
			newObj.supplierCreditorAccount = getSupplier.supplierCreditorAccount
		} else {
			newObj.supplierId = null
			newObj.supplierName = null
			newObj.supplierType = null
			newObj.supplierCreditorAccount = null
		}
		if (getProject) {
			newObj.projectName = getProject.name
			newObj.projectId = getProject.id
		} else {
			if (newObj.projectNumber.toLowerCase().trim() === 'overhead') {
				newObj.projectName = 'overhead'
				newObj.projectName = 'Overhead'
				newObj.projectId = 'overhead'
			} else {
				newObj.projectName = null
				newObj.projectId = null
			}
		}
		updatedData.push(newObj)
		return d
	})

	//Filter suppliers that don't have the required fields
	missingRequiredFields = updatedData?.filter(
		(d: any) =>
			!d.supplierId ||
			d.projectNumber.trim() === '' ||
			!d.total ||
			!d.netAmount ||
			!d.taxAmount
	)
	updatedData = updatedData?.filter(
		(d: any) =>
			d.supplierId &&
			d.projectNumber.trim() !== '' &&
			d.total &&
			d.netAmount &&
			d.taxAmount
	)

	noProjectWithProjectNumberFields = updatedData?.filter(
		(d: any) => !d.projectName
	)
	updatedData = updatedData?.filter((d: any) => d.projectName)
	suppliersNotIndividuals = updatedData?.filter(
		(d: any) => d.supplierType === 'company'
	)
	updatedData = updatedData?.filter((d: any) => d.supplierType !== 'company')

	const projectNameArray = updatedData.map((p: any) => {
		return p.projectName
	})

	//Add statuses to each array, to show the use what went wrong

	if (missingRequiredFields.length > 0) {
		missingRequiredFields = missingRequiredFields.map((i: any) => {
			const updateObj = {
				...i,
				importStatus: `${tExportInvoices('import.missingField')}`,
			}
			errorData.push(updateObj)
			return updateObj
		})
	}
	if (noProjectWithProjectNumberFields.length > 0) {
		noProjectWithProjectNumberFields = noProjectWithProjectNumberFields.map(
			(i: any) => {
				const updateObj = {
					...i,
					importStatus: `${tExportInvoices(
						'import.noProjectWithProjectNumber'
					)}`,
				}
				errorData.push(updateObj)
				return updateObj
			}
		)
	}
	if (suppliersNotIndividuals.length > 0) {
		suppliersNotIndividuals = suppliersNotIndividuals.map((i: any) => {
			const updateObj = {
				...i,
				importStatus: `${tExportInvoices(
					'import.suppliersNotIndividuals'
				)}`,
			}
			errorData.push(updateObj)
			return updateObj
		})
	}

	const finalData = [...updatedData]
	return { successData: finalData, failedData: errorData }
}
export const importExpenseReports = createAsyncThunk<
	any,
	{ suppliersName?: string[]; isMarkAsPaid: string | null | undefined },
	{ state: RootState; dispatch: ThunkAppDispatch; rejectValue: string }
>(
	'purchases/importExpenseReports',
	async (
		{ suppliersName, isMarkAsPaid },
		{ getState, dispatch, rejectWithValue }
	) => {
		const tExportInvoices = i18next.getFixedT(null, 'exportInvoices')
		try {
			const { auth, purchases } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User Table Info is not set')
			}
			const tenantId = userTableInfo.currentCompanyTenantId
			const results = purchases.importExpenseReportResultsData
			if (suppliersName && suppliersName?.length > 0) {
				for (const s of suppliersName) {
					let newSupplier: any = {
						id: nanoid(),
						tenantId: userTableInfo.currentCompanyTenantId,
						activeFlag: true,
						currency: 'EUR',
						supplierCreditorAccount:
							'601 - Achats stockés - Matières premières (et fournitures)',
						paymentTerms: 'net30',
						taxType: 'TVA',
						taxRate: '20',
						name: s,
						supplierType: 'individual',
						individualType: 'Casual Entertainment Worker',
					}
					let newSubAccount

					if (s.length > 6) {
						newSubAccount = `${s.substring(0, 6)}`
					} else {
						newSubAccount = `${s}`.padEnd(6, '0')
					}

					newSupplier.supplierSubAccount = newSubAccount

					// Default quote categories
					newSupplier.apfpCategoryUuid = categories.find(
						(i) => i.supplierDefault
					)?.categoryUuid
					newSupplier.postProdCategoryUuid =
						postProductionCategories.find(
							(i) => i.supplierDefault
						)?.categoryUuid
					newSupplier.aicpSubCategoryUuid = aicpSubCategories.find(
						(i) => i.supplierDefault
					)?.subCategoryUuid
					newSupplier.photoSubCategoryUuid =
						photoGridSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.soundProdSubCategoryUuid =
						soundProdSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.cnc5chSubCategoryUuid =
						cnc5chSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteAnimationOnlySubCategoryUuid =
						quoteAnimationOnlySubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteAnimationAndLiveActionSubCategoryUuid =
						quoteAnimationAndLiveActionSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier.quoteDigitalSubCategoryUuid =
						quoteDigitalSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

					newSupplier = getSupplierUuids({
						newSupplierData: newSupplier,
					})
					console.log('newSupplier: ', newSupplier)

					await API.graphql(
						graphqlOperation(mutations.createSuppliers, {
							input: newSupplier,
						})
					)
				}
			}
			const newSuppliersData =
				await fetchSuppliersByTenantIdUtil(tenantId)
			const projectData = await fetchProjectsByTenantIdUtil(tenantId)
			const missingRequiredFields: any = []
			const noProjectWithProjectNumberFields: any = []
			const suppliersNotIndividuals: any = []
			const updatedData: any = []
			const errorData: any = []
			const { successData, failedData } =
				await validateImportExpenseReportsData({
					results,
					newSuppliersData,
					projectData,
					missingRequiredFields,
					noProjectWithProjectNumberFields,
					suppliersNotIndividuals,
					updatedData,
					errorData,
				})
			console.log('failedData: ', failedData)
			console.log('successData: ', successData)
			for (const data of successData) {
				let type = 'importedERCostline'
				const supplierId = data.supplierId
				if (data.projectNumber === 'overhead') {
					type = 'importedEROverhead'
				}
				let currStatus = ''
				if (isMarkAsPaid === 'yes') {
					currStatus = 'Paid'
				} else {
					if (
						dayjs(data?.paymentDate, 'DD/MM/YYYY').diff(
							dayjs(),
							'day'
						) >= 0
					) {
						currStatus = 'Pending'
					} else if (
						dayjs(data?.paymentDate, 'DD/MM/YYYY').diff(
							dayjs(),
							'day'
						) < 0
					) {
						currStatus = 'Overdue'
					}
				}

				const newInvDate = data?.invoiceDate
					? dayjs(data?.invoiceDate, 'DD/MM/YYYY')
					: dayjs().toISOString()
				const newPaymentDate = data?.paymentDate
					? dayjs(data?.paymentDate, 'DD/MM/YYYY')
					: dayjs().toISOString()
				const newPaidDate = data?.paidDate
					? dayjs(data?.paidDate, 'DD/MM/YYYY')
					: dayjs().toISOString()
				const newNetAmtDetails = {
					projectName: data.projectName,
					projectId: data.projectId,
					projectNumber: data.projectNumber,
					netAmount: data.total,
					category: data.supplierCreditorAccount,
				}
				const newPreparePurchase = {
					tenantId: userTableInfo.currentCompanyTenantId,
					activeFlag: true,
					// invoiceName: '????',
					invoiceDate: newInvDate,
					paymentDate: newPaymentDate,
					paymentStatus: currStatus,
					preparePurchasePaidDate: newPaidDate,
					type: type,
					netAmountDetails: newNetAmtDetails,
					taxAmountDetails: [
						{
							taxAmount: data.taxAmount,
							vatAccount:
								'445660-TVA sur autres biens et services',
							vatIntracom: 'None',
						},
					],
					totalAmount: data.total,
					supplierId: supplierId,
					supplier: data.supplierName,
					currency: 'EUR',
					currentPage: 'export',
					lastExportedDate: dayjs().toISOString(),
				}
				console.log('newPreparePurchase: ', newPreparePurchase)
				await API.graphql(
					graphqlOperation(mutations.createPreparePurchase, {
						input: newPreparePurchase,
					})
				)
			}

			dispatch(fetchSuppliers())
			dispatch(
				preparePurchaseApi.util.invalidateTags([
					{ type: 'PreparePurchase', id: 'LIST' },
				])
			)
			makeSuccessToast(
				tExportInvoices('import.importedExportInvoicesSuccessfully')
			)
			return { successData: successData, errorData: failedData }
		} catch (err: any) {
			console.error('importExpenseReports thunk input', {
				suppliersName,
				isMarkAsPaid,
			})
			console.error('importExpenseReports thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const changeCurrentPreparePurchase = createAsyncThunk<
	ReduxPreparePurchase,
	{ preparePurchase: ReduxPreparePurchase },
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/changeCurrentPreparePurchase',
	async ({ preparePurchase }, { getState, rejectWithValue }) => {
		const { auth } = getState()
		try {
			return preparePurchase
		} catch (err: any) {
			console.error('changeCurrentPreparePurchase thunk input', {
				preparePurchase,
			})
			console.error('changeCurrentPreparePurchase thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const toggleSupplierInvoicesFlag = createAsyncThunk<
	void,
	{ preparePurchase: ReduxPreparePurchase },
	{
		state: RootState
		rejectValue: string
	}
>(
	'purchases/toggleSupplierInvoicesFlag',
	async ({ preparePurchase }, { getState, rejectWithValue }) => {
		const { auth } = getState()
		try {
			if (!preparePurchase || !preparePurchase?.id) return
			const {
				data: { getPreparePurchase: items },
			} = (await API.graphql(
				graphqlOperation(queries.getPreparePurchase, {
					id: preparePurchase?.id,
				})
			)) as { data: { getPreparePurchase: ReduxPreparePurchase } }
			const updObj = {
				id: items.id,
				flag: !items?.flag,
			}
			await API.graphql(
				graphqlOperation(mutations.updatePreparePurchase, {
					input: updObj,
				})
			)
		} catch (err: any) {
			console.error('toggleSupplierInvoicesFlag thunk input', {
				preparePurchase,
			})
			console.error('toggleSupplierInvoicesFlag thunk error', err)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const generateBridgePaymentLink = createAsyncThunk<
	void,
	{ rowId: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/generateBridgePaymentLink',
	async ({ rowId }, { dispatch, rejectWithValue, getState }) => {
		let errorMessage = msg
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			if (!rowId) {
				makeErrorToast('No line selected')
				return
			}
			const {
				data: {
					generatePaymentsLink: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.generatePaymentsLink, {
					companyTenantId: tenantId,
					rowId: rowId,
				})
			)) as {
				data: {
					generatePaymentsLink: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
				window.location.href = status
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				throwPurchasesStatusError(status, {
					action: 'generatePaymentsLink',
					companyTenantId: tenantId,
					rowId,
				})
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)
export const updatePaymentStatusAfterBridgePaymentLink = createAsyncThunk<
	void,
	{ paymentLinkId: string },
	{ dispatch: ThunkAppDispatch; rejectValue: string; state: RootState }
>(
	'purchases/updatePaymentStatusAfterBridgePaymentLink',
	async ({ paymentLinkId }, { dispatch, rejectWithValue, getState }) => {
		const errorMessage = msg
		const { auth } = getState()
		const userTableInfo = auth.userTableInfo
		if (!userTableInfo) {
			return rejectWithValue('User Table Info is not set')
		}
		const tenantId = userTableInfo.currentCompanyTenantId
		try {
			const {
				data: {
					preparePurchaseByBridgePaymentLinkId: { items },
				},
			} = (await API.graphql(
				graphqlOperation(queries.preparePurchaseByBridgePaymentLinkId, {
					bridgePaymentLinkId: paymentLinkId,
				})
			)) as {
				data: {
					preparePurchaseByBridgePaymentLinkId: {
						items: ReduxPurchase[]
					}
				}
			}
			console.log('items: ', items)
			if (
				items &&
				items.length > 0 &&
				items[0].paymentStatus !== 'Paid'
			) {
				const updObj = {
					id: items[0]?.id,
					paymentStatus: 'Paid',
				}
				await API.graphql(
					graphqlOperation(mutations.updatePreparePurchase, {
						input: updObj,
					})
				)
				dispatch(
					preparePurchaseApi.util.invalidateTags([
						{ type: 'PreparePurchase', id: 'LIST' },
					])
				)
			}
		} catch (err: any) {
			makeErrorToast(`Error : ${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const purchasesSlice = createSlice({
	name: 'purchases',
	initialState,
	reducers: {
		setConfirmUnmarkToPayDialogOpenStatus: (state, action) => {
			state.confirmUnmarkToPayDialogOpenStatus = action.payload.status
		},
		setConfirmExportToPayPreparePurchasesDialogOpenStatus: (
			state,
			action
		) => {
			state.confirmExportToPayPreparePurchasesDialogOpenStatus =
				action.payload.status
		},
		setConfirmExportPreparePurchaseXMLDialogOpenStatus: (state, action) => {
			state.confirmExportPreparePurchaseXMLDialogOpenStatus =
				action.payload.status
		},
		setConfirmSelectAllInExportDialogOpenStatus: (state, action) => {
			state.confirmSelectAllInExportDialogOpenStatus =
				action.payload.status
		},
		setConfirmRemoveEmailInvoiceDialogOpenStatus: (state, action) => {
			state.removeEmailInvoiceDialogStatus = action.payload.status
		},
		setSupplierRedirectConfirmationDialogOpenStatus: (state, action) => {
			state.supplierRedirectConfirmationDialogOpenStatus =
				action.payload.status
		},
		setPurchaseArchivedConfirmationDialogOpenStatus: (state, action) => {
			state.purchaseArchivedConfirmationDialogOpenStatus =
				action.payload.status
		},
		setPrepareInvoiceArchiveConfirmationDialogOpenStatus: (
			state,
			action
		) => {
			state.prepareInvoiceArchiveConfirmationDialogOpenStatus =
				action.payload.status
		},
		setReviewInvoiceNoCostlineConfirmationDialogOpenStatus: (
			state,
			action
		) => {
			state.reviewInvoiceNoCostlineConfirmationDialogOpenStatus =
				action.payload.status
		},
		setPurchasePaidConfirmationDialogOpenStatus: (state, action) => {
			state.purchasePaidConfirmationDialogOpenStatus =
				action.payload.status
		},
		setPurchaseUnpaidConfirmationDialogOpenStatus: (state, action) => {
			state.purchaseUnpaidConfirmationDialogOpenStatus =
				action.payload.status
		},
		setPurchaseDownloadConfirmationDialogOpenStatus: (state, action) => {
			state.purchaseDownloadConfirmationDialogOpenStatus =
				action.payload.status
		},
		setMoveToPrepareModalOpenStatus: (state, action) => {
			state.moveToPrepareModalOpenStatus = action.payload.status
		},
		setMoveToReviewOpenStatus: (state, action) => {
			state.moveToReviewOpenStatus = action.payload.status
		},
		setFetchPurchaseApiStatus: (state, action) => {
			state.fetchPurchasesApiStatus = 'loading'
		},
		setAddInvoiceModalOpenStatus: (state, action) => {
			state.addInvoiceModalOpenStatus = action.payload.status
		},
		setNewInvoiceModalOpenStatus: (state, action) => {
			state.newInvoiceModalOpenStatus = action.payload.status
		},
		setAssignInvoiceModalOpenStatus: (state, action) => {
			state.assignInvoiceModalOpenStatus = action.payload.status
		},
		setConfirmInvoiceModalOpenStatus: (state, action) => {
			state.confirmInvoiceModalOpenStatus = action.payload.status
		},
		setAddEmailInvoiceModalOpenStatus: (state, action) => {
			state.addEmailInvoiceModalOpenStatus = action.payload.status
		},
		setEditInvoiceModalOpenStatus: (state, action) => {
			state.editInvoiceModalOpenStatus = action.payload.status
		},
		setEditPrepareInvoiceModalOpenStatus: (state, action) => {
			state.editPrepareInvoiceModalOpenStatus = action.payload.status
		},
		setCurrentPurchase: (state, action) => {
			state.currentPurchaseInfo = action.payload
		},
		setCurrentPreparePurchase: (state, action) => {
			state.currentPreparePurchase = action.payload
			state.changeCurrentPreparePurchaseInvoiceApiStatus = 'succeeded'
		},
		setDeleteInvoiceDialogOpenStatus: (state, action) => {
			state.deleteInvoiceDialogOpenStatus = action.payload.status
		},
		setAddTagsModalOpenStatus: (state, action) => {
			state.addTagsModalOpenStatus = action.payload.status
		},
		setConfirmExportDialogOpenStatus: (state, action) => {
			state.confirmExportDialogOpenStatus = action.payload.status
		},
		setFormData: (state, action) => {
			state.formData = action.payload
		},
		setImportExportInvoicesModalOpen: (state, action) => {
			state.importExportInvoicesModalOpen = action.payload
		},
		setImportExportInvoicesAddSuppliersModalOpen: (state, action) => {
			state.importExportInvoicesAddSuppliersModalOpen = action.payload
			state.importExportInvoicesModalOpen = false
		},
		setImportSuppliersInExportInvoicesData: (state, action) => {
			state.importSuppliersInExportInvoicesData = action.payload
		},
		setImportExportInvoicesResultsData: (state, action) => {
			state.importExportInvoicesResultsData = action.payload
		},
		setImportExportInvoicesMessageDialogOpen: (state, action) => {
			state.importExportInvoicesMessageDialogOpen = action.payload
		},
		setPurchaseGridLoading: (state, action) => {
			state.purchaseGridLoading = action.payload
		},
		setIsBulkAction: (state, action) => {
			state.isBulkAction = action.payload
		},
		setImportExpenseReportsModalOpen: (state, action) => {
			state.importExpenseReportsModalOpen = action.payload
		},
		setImportExpenseReportResultsData: (state, action) => {
			state.importExpenseReportResultsData = action.payload
		},
		setImportExpenseReportsMessageDialogOpen: (state, action) => {
			state.importExpenseReportsMessageDialogOpen = action.payload
		},
		setClearCurrentPurchase: (state, action) => {
			state.currentPurchaseInfo = null
		},
		setBridgePaymentLinkDialogOpenStatus: (state, action) => {
			state.bridgePaymentLinkDialogOpenStatus = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchCurrentPurchase.pending, (state, action) => {
			state.fetchCurrentPurchaseApiStatus = 'loading'
		})
		builder.addCase(fetchCurrentPurchase.fulfilled, (state, action) => {
			state.fetchCurrentPurchaseApiStatus = 'succeeded'
			state.currentPurchaseInfo = action.payload
		})
		builder.addCase(fetchCurrentPurchase.rejected, (state, action) => {
			state.fetchCurrentPurchaseApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			fetchCurrentPreparePurchase.pending,
			(state, action) => {
				state.fetchCurrentPreparePurchaseApiStatus = 'loading'
			}
		)
		builder.addCase(
			fetchCurrentPreparePurchase.fulfilled,
			(state, action) => {
				state.fetchCurrentPreparePurchaseApiStatus = 'succeeded'
				state.currentPreparePurchase = action.payload
			}
		)
		builder.addCase(
			fetchCurrentPreparePurchase.rejected,
			(state, action) => {
				state.fetchCurrentPreparePurchaseApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(fetchPurchases.pending, (state, action) => {
			state.fetchPurchasesApiStatus = 'loading'
		})
		builder.addCase(fetchPurchases.fulfilled, (state, action) => {
			state.fetchPurchasesApiStatus = 'succeeded'
			state.purchasesData = action.payload.sortedItems
			state.quoteData = action.payload.quoteData
			state.projectData = action.payload.projectData
		})
		builder.addCase(fetchPurchases.rejected, (state, action) => {
			state.fetchPurchasesApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchAllPreparePurchases.pending, (state, action) => {
			state.fetchAllPreparePurchasesApiStatus = 'loading'
		})
		builder.addCase(fetchAllPreparePurchases.fulfilled, (state, action) => {
			state.fetchAllPreparePurchasesApiStatus = 'succeeded'
			state.allPreparePurchasesData = action.payload
		})
		builder.addCase(fetchAllPreparePurchases.rejected, (state, action) => {
			state.fetchAllPreparePurchasesApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			fetchReviewAndExportPreparePurchases.pending,
			(state, action) => {
				state.fetchReviewAndExportPreparePurchasesApiStatus = 'loading'
			}
		)
		builder.addCase(
			fetchReviewAndExportPreparePurchases.fulfilled,
			(state, action) => {
				state.fetchReviewAndExportPreparePurchasesApiStatus =
					'succeeded'
				state.preparePurchasesInExportAndReview = action.payload
			}
		)
		builder.addCase(
			fetchReviewAndExportPreparePurchases.rejected,
			(state, action) => {
				state.fetchReviewAndExportPreparePurchasesApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(fetchUnpaidExportPurchases.pending, (state, action) => {
			state.fetchUnpaidExportPurchasesApiStatus = 'loading'
		})
		builder.addCase(
			fetchUnpaidExportPurchases.fulfilled,
			(state, action) => {
				state.fetchUnpaidExportPurchasesApiStatus = 'succeeded'
				state.unpaidPurchasesInExport = action.payload
			}
		)
		builder.addCase(
			fetchUnpaidExportPurchases.rejected,
			(state, action) => {
				state.fetchUnpaidExportPurchasesApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(fetchPurchasesFreelancer.pending, (state, action) => {
			state.fetchPurchasesApiStatus = 'loading'
		})
		builder.addCase(fetchPurchasesFreelancer.fulfilled, (state, action) => {
			state.fetchPurchasesApiStatus = 'succeeded'
			state.purchasesData = action.payload.sortedItems
			state.quoteData = action.payload.quoteData
			state.projectData = action.payload.projectData
		})
		builder.addCase(fetchPurchasesFreelancer.rejected, (state, action) => {
			state.fetchPurchasesApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createPurchases.pending, (state, action) => {
			state.createPurchaseApiStatus = 'loading'
		})
		builder.addCase(createPurchases.fulfilled, (state, action) => {
			state.createPurchaseApiStatus = 'succeeded'
			state.purchaseGridLoading = false
		})
		builder.addCase(createPurchases.rejected, (state, action) => {
			state.createPurchaseApiStatus = 'failed'
			state.purchaseGridLoading = false
			state.error = action.error.message
		})
		builder.addCase(updatePurchase.pending, (state, action) => {
			state.updatePurchaseApiStatus = 'loading'
		})
		builder.addCase(updatePurchase.fulfilled, (state, action) => {
			state.updatePurchaseApiStatus = 'succeeded'
			state.purchaseGridLoading = false
		})
		builder.addCase(updatePurchase.rejected, (state, action) => {
			state.updatePurchaseApiStatus = 'failed'
			state.error = action.error.message
			state.purchaseGridLoading = false
		})
		builder.addCase(updatePurchaseTags.pending, (state, action) => {
			state.updatePurchaseApiStatus = 'loading'
		})
		builder.addCase(updatePurchaseTags.fulfilled, (state, action) => {
			state.updatePurchaseApiStatus = 'succeeded'
			state.addTagsModalOpenStatus = false
		})
		builder.addCase(updatePurchaseTags.rejected, (state, action) => {
			state.updatePurchaseApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(updatePaymentStatus.pending, (state, action) => {
			state.updatePaymentApiStatus = 'loading'
		})
		builder.addCase(updatePaymentStatus.fulfilled, (state, action) => {
			state.updatePaymentApiStatus = 'succeeded'
			state.purchasePaidConfirmationDialogOpenStatus = false
			state.purchaseUnpaidConfirmationDialogOpenStatus = false
		})
		builder.addCase(updatePaymentStatus.rejected, (state, action) => {
			state.updatePaymentApiStatus = 'failed'
			state.purchasePaidConfirmationDialogOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(dailyUpdatePaymentStatus.pending, (state, action) => {
			state.dailyUpdatePaymentApiStatus = 'loading'
		})
		builder.addCase(dailyUpdatePaymentStatus.fulfilled, (state, action) => {
			state.dailyUpdatePaymentApiStatus = 'succeeded'
		})
		builder.addCase(dailyUpdatePaymentStatus.rejected, (state, action) => {
			state.dailyUpdatePaymentApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(moveToNext.pending, (state, action) => {
			state.updateMoveToNextApiStatus = 'loading'
		})
		builder.addCase(moveToNext.fulfilled, (state, action) => {
			state.updateMoveToNextApiStatus = 'succeeded'
			state.confirmInvoiceModalOpenStatus = false
		})
		builder.addCase(moveToNext.rejected, (state, action) => {
			state.updateMoveToNextApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(updateLastExportedDate.pending, (state, action) => {
			state.updateLastExportedDateApiStatus = 'loading'
		})
		builder.addCase(updateLastExportedDate.fulfilled, (state, action) => {
			state.updateLastExportedDateApiStatus = 'succeeded'
			state.confirmInvoiceModalOpenStatus = false
		})
		builder.addCase(updateLastExportedDate.rejected, (state, action) => {
			state.updateLastExportedDateApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			handleMoveToPrepareFromExport.pending,
			(state, action) => {
				state.moveToPrepareFromExportApiStatus = 'loading'
			}
		)
		builder.addCase(
			handleMoveToPrepareFromExport.fulfilled,
			(state, action) => {
				state.moveToPrepareFromExportApiStatus = 'succeeded'
				state.prepareInvoiceArchiveConfirmationDialogOpenStatus = false
				state.moveToReviewOpenStatus = false
			}
		)
		builder.addCase(
			handleMoveToPrepareFromExport.rejected,
			(state, action) => {
				state.moveToPrepareFromExportApiStatus = 'failed'
				state.error = action.error.message
			}
		)

		builder.addCase(assign.pending, (state, action) => {
			state.updateAssignApiStatus = 'loading'
		})
		builder.addCase(assign.fulfilled, (state, action) => {
			state.updateAssignApiStatus = 'succeeded'
			state.assignInvoiceModalOpenStatus = false
			state.reviewInvoiceNoCostlineConfirmationDialogOpenStatus = false
			state.confirmInvoiceModalOpenStatus = false
			state.formData = null
		})
		builder.addCase(assign.rejected, (state, action) => {
			state.updateAssignApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(assignDirectlyToExports.pending, (state, action) => {
			state.updateAssignDirectlyToExportsApiStatus = 'loading'
		})
		builder.addCase(assignDirectlyToExports.fulfilled, (state, action) => {
			state.updateAssignDirectlyToExportsApiStatus = 'succeeded'
			state.assignDirectlyToExportsInvoiceModalOpenStatus = false
			state.reviewInvoiceNoCostlineConfirmationDialogOpenStatus = false
			state.confirmInvoiceModalOpenStatus = false
		})
		builder.addCase(assignDirectlyToExports.rejected, (state, action) => {
			state.updateAssignDirectlyToExportsApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(moveToPrev.pending, (state, action) => {
			state.updateMoveToPrevApiStatus = 'loading'
		})
		builder.addCase(moveToPrev.fulfilled, (state, action) => {
			state.updateMoveToPrevApiStatus = 'succeeded'
			state.moveToPrepareModalOpenStatus = false
			state.moveToReviewOpenStatus = false
		})
		builder.addCase(moveToPrev.rejected, (state, action) => {
			state.updateMoveToPrevApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(moveBackToReview.pending, (state, action) => {
			state.updateMoveBackToReviewApiStatus = 'loading'
		})
		builder.addCase(moveBackToReview.fulfilled, (state, action) => {
			state.updateMoveBackToReviewApiStatus = 'succeeded'
			state.moveToReviewOpenStatus = false
		})
		builder.addCase(moveBackToReview.rejected, (state, action) => {
			state.updateMoveBackToReviewApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(toggleArchive.pending, (state, action) => {
			state.updateToggleArchiveApiStatus = 'loading'
		})
		builder.addCase(toggleArchive.fulfilled, (state, action) => {
			state.updateToggleArchiveApiStatus = 'succeeded'
			state.prepareInvoiceArchiveConfirmationDialogOpenStatus = false
		})
		builder.addCase(toggleArchive.rejected, (state, action) => {
			state.updateToggleArchiveApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(editPreparePurchase.pending, (state, action) => {
			state.editPrepareInvoiceApiStatus = 'loading'
		})
		builder.addCase(editPreparePurchase.fulfilled, (state, action) => {
			state.editPrepareInvoiceApiStatus = 'succeeded'
			state.confirmInvoiceModalOpenStatus = false
			state.editPrepareInvoiceModalOpenStatus = false
		})
		builder.addCase(editPreparePurchase.rejected, (state, action) => {
			state.editPrepareInvoiceApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(deleteInvoice.pending, (state, action) => {
			state.deletePurchaseInvoiceApiStatus = 'loading'
		})
		builder.addCase(deleteInvoice.fulfilled, (state, action) => {
			state.deletePurchaseInvoiceApiStatus = 'succeeded'
		})
		builder.addCase(deleteInvoice.rejected, (state, action) => {
			state.deletePurchaseInvoiceApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(markAsToPay.pending, (state, action) => {
			state.markAsToPayApiStatus = 'loading'
		})
		builder.addCase(markAsToPay.fulfilled, (state, action) => {
			state.markAsToPayApiStatus = 'succeeded'
		})
		builder.addCase(markAsToPay.rejected, (state, action) => {
			state.markAsToPayApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(bulkUndoToPay.pending, (state, action) => {
			state.bulkUndoToPayApiStatus = 'loading'
		})
		builder.addCase(bulkUndoToPay.fulfilled, (state, action) => {
			state.bulkUndoToPayApiStatus = 'succeeded'
		})
		builder.addCase(bulkUndoToPay.rejected, (state, action) => {
			state.bulkUndoToPayApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(bulkUndoPendingRecon.pending, (state, action) => {
			state.bulkUndoPendingReconApiStatus = 'loading'
		})
		builder.addCase(bulkUndoPendingRecon.fulfilled, (state, action) => {
			state.bulkUndoPendingReconApiStatus = 'succeeded'
		})
		builder.addCase(bulkUndoPendingRecon.rejected, (state, action) => {
			state.bulkUndoPendingReconApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(undoToPay.pending, (state, action) => {
			state.undoToPayApiStatus = 'loading'
		})
		builder.addCase(undoToPay.fulfilled, (state, action) => {
			state.undoToPayApiStatus = 'succeeded'
			state.confirmUnmarkToPayDialogOpenStatus = false
		})
		builder.addCase(undoToPay.rejected, (state, action) => {
			state.undoToPayApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(bulkMarkAsPaid.pending, (state, action) => {
			state.bulkMarkAsPaidApiStatus = 'loading'
		})
		builder.addCase(bulkMarkAsPaid.fulfilled, (state, action) => {
			state.bulkMarkAsPaidApiStatus = 'succeeded'
			state.purchasePaidConfirmationDialogOpenStatus = false
			state.isBulkAction = false
		})
		builder.addCase(bulkMarkAsPaid.rejected, (state, action) => {
			state.bulkMarkAsPaidApiStatus = 'failed'
			state.purchasePaidConfirmationDialogOpenStatus = false
			state.isBulkAction = false
			state.error = action.error.message
		})
		builder.addCase(bulkUnmarkAsPaid.pending, (state, action) => {
			state.bulkUnmarkAsPaidApiStatus = 'loading'
		})
		builder.addCase(bulkUnmarkAsPaid.fulfilled, (state, action) => {
			state.bulkUnmarkAsPaidApiStatus = 'succeeded'
			state.purchaseUnpaidConfirmationDialogOpenStatus = false
			state.isBulkAction = false
		})
		builder.addCase(bulkUnmarkAsPaid.rejected, (state, action) => {
			state.bulkUnmarkAsPaidApiStatus = 'failed'
			state.purchaseUnpaidConfirmationDialogOpenStatus = false
			state.isBulkAction = false
			state.error = action.error.message
		})
		builder.addCase(importExportInvoices.pending, (state, action) => {
			state.importExportInvoicesInvoiceApiStatus = 'loading'
		})
		builder.addCase(importExportInvoices.fulfilled, (state, action) => {
			state.importExportInvoicesInvoiceApiStatus = 'succeeded'
			state.importErrorData = action.payload.errorData
			state.importSuccessData = action.payload.successData
			state.importExportInvoicesAddSuppliersModalOpen = false
			state.importExportInvoicesMessageDialogOpen = true
			state.importExportInvoicesModalOpen = false
		})
		builder.addCase(importExportInvoices.rejected, (state, action) => {
			state.importExportInvoicesInvoiceApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(importExpenseReports.pending, (state, action) => {
			state.importExpenseReportsInvoiceApiStatus = 'loading'
		})
		builder.addCase(importExpenseReports.fulfilled, (state, action) => {
			state.importExpenseReportsInvoiceApiStatus = 'succeeded'
			state.importExpenseReportErrorData = action.payload.errorData
			state.importExpenseReportSuccessData = action.payload.successData
			state.importExportInvoicesAddSuppliersModalOpen = false
			state.importExpenseReportsMessageDialogOpen = true
			state.importExpenseReportsModalOpen = false
		})
		builder.addCase(importExpenseReports.rejected, (state, action) => {
			state.importExpenseReportsInvoiceApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(generateBridgePaymentLink.pending, (state, action) => {
			state.generateBridgePaymentLinkApiStatus = 'loading'
		})
		builder.addCase(
			generateBridgePaymentLink.fulfilled,
			(state, action) => {
				state.generateBridgePaymentLinkApiStatus = 'succeeded'
			}
		)
		builder.addCase(generateBridgePaymentLink.rejected, (state, action) => {
			state.generateBridgePaymentLinkApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			updatePaymentStatusAfterBridgePaymentLink.pending,
			(state, action) => {
				state.updatePaymentStatusAfterBridgePaymentLinkApiStatus =
					'loading'
			}
		)
		builder.addCase(
			updatePaymentStatusAfterBridgePaymentLink.fulfilled,
			(state, action) => {
				state.updatePaymentStatusAfterBridgePaymentLinkApiStatus =
					'succeeded'
			}
		)
		builder.addCase(
			updatePaymentStatusAfterBridgePaymentLink.rejected,
			(state, action) => {
				state.updatePaymentStatusAfterBridgePaymentLinkApiStatus =
					'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(
			changeCurrentPreparePurchase.pending,
			(state, action) => {
				state.changeCurrentPreparePurchaseInvoiceApiStatus = 'loading'
			}
		)
		builder.addCase(
			changeCurrentPreparePurchase.fulfilled,
			(state, action) => {
				state.changeCurrentPreparePurchaseInvoiceApiStatus = 'succeeded'
				state.currentPreparePurchase = action.payload
			}
		)
		builder.addCase(
			changeCurrentPreparePurchase.rejected,
			(state, action) => {
				state.changeCurrentPreparePurchaseInvoiceApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(toggleSupplierInvoicesFlag.pending, (state, action) => {
			state.toggleSupplierInvoicesFlagInvoiceApiStatus = 'loading'
		})
		builder.addCase(
			toggleSupplierInvoicesFlag.fulfilled,
			(state, action) => {
				state.toggleSupplierInvoicesFlagInvoiceApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			toggleSupplierInvoicesFlag.rejected,
			(state, action) => {
				state.toggleSupplierInvoicesFlagInvoiceApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addMatcher(
			preparePurchaseApi.endpoints.createPreparePurchaseById
				.matchFulfilled,
			(state) => {
				state.createPreparePurchaseApiStatus = 'succeeded'
				state.newInvoiceModalOpenStatus = false
				state.addEmailInvoiceModalOpenStatus = false
			}
		)
		builder.addMatcher(
			purchaseApi.endpoints.fetchPurchaseById.matchFulfilled,
			(state, { payload }) => {
				state.fetchCurrentPurchaseApiStatus = 'succeeded'
				state.currentPurchaseInfo = payload
			}
		)
	},
})

export const {
	setConfirmUnmarkToPayDialogOpenStatus,
	setConfirmExportToPayPreparePurchasesDialogOpenStatus,
	setConfirmExportPreparePurchaseXMLDialogOpenStatus,
	setConfirmSelectAllInExportDialogOpenStatus,
	setConfirmRemoveEmailInvoiceDialogOpenStatus,
	setSupplierRedirectConfirmationDialogOpenStatus,
	setPurchaseArchivedConfirmationDialogOpenStatus,
	setPrepareInvoiceArchiveConfirmationDialogOpenStatus,
	setReviewInvoiceNoCostlineConfirmationDialogOpenStatus,
	setPurchasePaidConfirmationDialogOpenStatus,
	setPurchaseUnpaidConfirmationDialogOpenStatus,
	setPurchaseDownloadConfirmationDialogOpenStatus,
	setMoveToPrepareModalOpenStatus,
	setMoveToReviewOpenStatus,
	setAddInvoiceModalOpenStatus,
	setNewInvoiceModalOpenStatus,
	setAssignInvoiceModalOpenStatus,
	setConfirmInvoiceModalOpenStatus,
	setAddEmailInvoiceModalOpenStatus,
	setEditInvoiceModalOpenStatus,
	setEditPrepareInvoiceModalOpenStatus,
	setCurrentPurchase,
	setCurrentPreparePurchase,
	setDeleteInvoiceDialogOpenStatus,
	setAddTagsModalOpenStatus,
	setConfirmExportDialogOpenStatus,
	setFormData,
	setImportExportInvoicesModalOpen,
	setImportExportInvoicesAddSuppliersModalOpen,
	setImportSuppliersInExportInvoicesData,
	setImportExportInvoicesResultsData,
	setImportExportInvoicesMessageDialogOpen,
	setIsBulkAction,
	setImportExpenseReportsModalOpen,
	setImportExpenseReportResultsData,
	setImportExpenseReportsMessageDialogOpen,
	// setImportPurchasesModalOpen,
	// setImportPurchasesAddSuppliersModalOpen,
	// setImportSuppliersInPurchasesData,
	// setImportPurchasesResultsData,
	// setImportPurchasesMessageDialogOpen,
	setPurchaseGridLoading,
	setClearCurrentPurchase,
	setBridgePaymentLinkDialogOpenStatus,
} = purchasesSlice.actions

export default purchasesSlice.reducer
